/**
 * AppointmentScheduleDialog
 *
 * Calendar Stage 10 — Lifted out of App.js (~2,800 lines of JSX shell).
 * For Commit 1 this is a verbatim JSX shell extraction (Option A). All
 * Dashboard-scoped state, helpers, and refresh callbacks come in via the
 * `legacy` prop; core appointment/static/clients data is pulled from
 * canonical contexts directly so call-sites don't need to ferry it.
 *
 * Future commits (Stage 10 part 2+) will iteratively migrate the state
 * + effects + handler closures INTO this file once the JSX shell has
 * proven stable.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppointmentAssessmentsSection } from './AssessmentQuickSend';
import { useArchivedProviders, ArchivedProvidersToggleRow, ArchivedProviderTag } from '../shared/ArchivedProvidersSupport';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { getBusinessHours, isOutsideBusinessHours, formatHHMM12 } from '../../utils/businessHours';
import { ChangeHistoryModal } from './ChangeHistoryModal';
import { AttendanceTrackingDialog } from './groups/AttendanceTrackingDialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DateTimeField, DateField } from './DateTimeField';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  CheckCircle, ChevronDown, Copy, ExternalLink, FileText, Folder, History, MapPin, Shield,
  UserPlus, Users, Video, X,
} from 'lucide-react';

import { DebouncedTextarea, DebouncedInput } from '../ui/debounced-textarea';
import { AddonCptPicker } from '../shared/AddonCptPicker';
import { AppointmentLinkedItemsPanel } from '../shared/AppointmentLinkedItemsPanel';
import { CptCombobox } from '../shared/CptCombobox';
import { IntakeBundlePrefillDialog } from './IntakeBundlePrefillDialog';

import { useAuth } from '../../context/AuthContext';
import { useUserRoleName } from '../../hooks/useUserRoleName';
import { useAppointments } from '../../context/AppointmentsContext';
import { useStaticData } from '../../context/StaticDataContext';
import { useClients } from '../../context/ClientsContext';
import { useNotes } from '../../context/NotesContext';
import { useImpersonation } from '../../context/ImpersonationContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Jun 2026 (user request) — standard CMS POS descriptions used as a
// fallback so the POS dropdown can never render "Unknown" while the
// shared pos-codes list is still loading (e.g., V2 deep links).
const STANDARD_POS_DESCRIPTIONS = {
  '02': "Telehealth Provided Other than in Patient's Home",
  '03': 'School',
  '04': 'Homeless Shelter',
  '10': "Telehealth Provided in Patient's Home",
  '11': 'Office',
  '12': 'Home',
  '13': 'Assisted Living Facility',
  '14': 'Group Home',
  '15': 'Mobile Unit',
  '17': 'Walk-in Retail Health Clinic',
  '19': 'Off Campus - Outpatient Hospital',
  '20': 'Urgent Care Facility',
  '21': 'Inpatient Hospital',
  '22': 'On Campus - Outpatient Hospital',
  '23': 'Emergency Room - Hospital',
  '31': 'Skilled Nursing Facility',
  '32': 'Nursing Facility',
  '33': 'Custodial Care Facility',
  '49': 'Independent Clinic',
  '50': 'Federally Qualified Health Center',
  '51': 'Inpatient Psychiatric Facility',
  '52': 'Psychiatric Facility - Partial Hospitalization',
  '53': 'Community Mental Health Center',
  '54': 'Intermediate Care Facility',
  '55': 'Residential Substance Abuse Treatment Facility',
  '56': 'Psychiatric Residential Treatment Center',
  '57': 'Non-residential Substance Abuse Treatment Facility',
  '58': 'Non-residential Opioid Treatment Facility',
  '60': 'Mass Immunization Center',
  '62': 'Comprehensive Outpatient Rehabilitation Facility',
  '65': 'End-Stage Renal Disease Treatment Facility',
  '71': 'Public Health Clinic',
  '72': 'Rural Health Clinic',
  '99': 'Other Place of Service',
};

/**
 * Single-prop bag (`legacy`) that ferries Dashboard-scoped identifiers
 * the extracted JSX still references via closure. As state migrates into
 * this component these will shrink.  Anything available from a canonical
 * context (user, appointments, clients, providers, ...) is pulled from
 * the context directly — NOT through `legacy` — to keep call-sites tidy.
 */
export const AppointmentScheduleDialog = ({ legacy }) => {
  // ── Canonical context data ─────────────────────────────────────────
  const { user } = useAuth();
  const userRoleName = useUserRoleName();
  const {
    appointments, setAppointments,
    showAppointmentForm, setShowAppointmentForm,
    editingAppointment, setEditingAppointment,
    appointmentFormData, setAppointmentFormData,
    addAttendeeAppointment, setAddAttendeeAppointment,
    groupNoteAppointment, setGroupNoteAppointment,
  } = useAppointments();
  // Linked-note delete confirmation (Jun 2026 — replaces the native
  // "Cancel to unlink" window.confirm with a proper 3-action dialog).
  const [linkedNoteDeleteInfo, setLinkedNoteDeleteInfo] = useState(null);
  const performLinkedNoteDelete = async (deleteNote) => {
    const appt = linkedNoteDeleteInfo?.appointment;
    const noteId = linkedNoteDeleteInfo?.noteId;
    if (!appt) return;
    try {
      const resp = await axios.delete(`${API}/appointments/${appt.id}?delete_note=${deleteNote}`);
      if (deleteNote && noteId) {
        // Jun 2026 — undoable via Ctrl+Z: restores appointment AND note, link intact.
        addToUndoStack('delete_with_note', { appointment: appt, note_id: noteId });
        toast.success(`${resp.data.message} — press Ctrl+Z to undo`, { duration: 7000 });
      } else {
        addToUndoStack('delete', appt);
        toast.success(resp.data.message);
      }
      setLinkedNoteDeleteInfo(null);
      setShowAppointmentForm(false);
      setEditingAppointment(null);
      refreshAppointments(true); refreshStats(); refreshNotes();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      const detail = error.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : (detail?.message || 'Failed to delete appointment'),
        { duration: 7000 }
      );
      setLinkedNoteDeleteInfo(null);
      refreshAppointments(true);
    }
  };
  const {
    providers, users, cptCodes, posCodes, serviceTypes, locations,
    organizations, intakeBundles, cptBundles,
  } = useStaticData();
  const {
    clients, clientGroups, clientNotes, setClientNotes, allClientsForLookup,
    clientAuthorizations, selectedClient,
  } = useClients();
  const { notes, setNotes } = useNotes();
  const { impersonatedUserId } = useImpersonation();

  // Jun 2026 — optional archived-provider visibility in the provider
  // dropdown (off by default, resets whenever the dialog closes).
  const { includeArchived, toggleArchived, resetArchived, mergeProviders, archivedProviders } = useArchivedProviders();
  useEffect(() => { if (!showAppointmentForm) resetArchived(); }, [showAppointmentForm, resetArchived]);

  // Jun 2026 — managed Internal Appointment Types (Org Settings card).
  // Drives the Supervision / Internal entries of the type dropdown.
  const [internalTypes, setInternalTypes] = useState([]);
  useEffect(() => {
    if (!showAppointmentForm || internalTypes.length > 0) return;
    // include_inactive so an appointment whose managed type was later
    // deactivated still renders its own type (with "(inactive)" suffix).
    axios.get(`${API}/internal-appointment-types?include_inactive=true`)
      .then(({ data }) => setInternalTypes(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [showAppointmentForm]);
  const canApprovePayroll = !!(
    user?.is_admin || user?.is_super_admin || user?.is_payroll_manager
    || ['admin', 'super admin'].includes((user?.role || '').toLowerCase())
  );
  const selectedInternalType = internalTypes.find(
    (t) => t.id === appointmentFormData.internal_type_id,
  ) || internalTypes.find(
    (t) => !appointmentFormData.internal_type_id && t.slug === appointmentFormData.appointment_type,
  ) || null;
  const internalTypeSelectValue = selectedInternalType
    ? `itype:${selectedInternalType.id}`
    : appointmentFormData.appointment_type;

  // ── Dashboard-scoped legacy bag ────────────────────────────────────
  const {
    // helpers
    isSuperAdmin,
    getEffectivePosCodes,
    closeAppointment,
    fetchAppointments,
    handleCreateAppointment,
    refreshAppointments,
    refreshNotes,
    refreshTodos,
    refreshStats,
    openMeetPopup,
    openNote,
    openNoteById,
    navigate,
    getAccessibleClients,
    getAccessibleProviders,
    getAccessibleCPTCodes,
    getDOSEligibilityDisplay,
    getEligibilityDisplay,
    eligibilityTooltip,
    handleEligibilityCheck,
    handleOpenAttendanceDialog,
    formatClientName,
    addToUndoStack,
    safeEligibilityDetail,
    // Dashboard-local state setters used inside the dialog
    applicableIntakeBundles,
    setApplicableIntakeBundles,
    setChangeHistoryEntity,
    setShowChangeHistory,
    showGroupNoteDialog, setShowGroupNoteDialog,
    groupNoteGroup, setGroupNoteGroup,
    groupNoteCurriculumSession, setGroupNoteCurriculumSession,
    groupNoteExistingNotes, setGroupNoteExistingNotes,
    groupNoteMode, setGroupNoteMode,
    // Dashboard-local data
    allAuthorizations,
  } = legacy || {};

  // ── V2 self-contained Change History + Attendance (Jun 2026) ───────
  // The V2 ctx bridges pass null for the legacy dashboard-level setters,
  // which made the "Change History" link and group "Attendance" button
  // silently do nothing in V2 (user report). Host both dialogs locally
  // and fall back to them whenever the legacy setters aren't provided.
  const [localHistoryEntity, setLocalHistoryEntity] = useState(null);
  const [localAttendanceOpen, setLocalAttendanceOpen] = useState(false);
  const [localAttendanceApt, setLocalAttendanceApt] = useState(null);
  const [localAttendanceData, setLocalAttendanceData] = useState({});
  const [localAttendanceGroup, setLocalAttendanceGroup] = useState(null);
  // Add-attendee dialog — internalized (Jun 2026 V2 parity): the ctx-driven
  // version was only mounted by the legacy dashboard, so the "Attendee"
  // button silently no-op'd on every V2 surface. One code path everywhere.
  const [showAddAttendeeLocal, setShowAddAttendeeLocal] = useState(false);
  const [addAttendeeSearchLocal, setAddAttendeeSearchLocal] = useState('');
  const [addAttendeeApptLocal, setAddAttendeeApptLocal] = useState(null);
  const findAttGroup = (apt, fetched) =>
    (clientGroups || []).find((x) => x.id === apt?.selected_group_id)
    || (fetched && fetched.id === apt?.selected_group_id ? fetched : null);
  const openLocalAttendance = async (apt) => {
    let g = findAttGroup(apt, localAttendanceGroup);
    // V2 routes don't preload clientGroups — fetch the group directly.
    if (!g && apt?.selected_group_id) {
      try {
        const { data } = await axios.get(`${API}/client-groups/${apt.selected_group_id}`);
        if (data?.id) { g = data; setLocalAttendanceGroup(data); }
      } catch { /* dialog will show group-not-found */ }
    }
    const init = {};
    if (apt?.client_attendance && Object.keys(apt.client_attendance).length > 0) {
      Object.assign(init, apt.client_attendance);
    } else {
      (g?.members || []).filter((m) => m.status === 'active' || !m.status)
        .forEach((m) => { init[m.client_id] = 'pending'; });
      (g?.client_ids || []).forEach((id) => { if (!(id in init)) init[id] = 'pending'; });
    }
    setLocalAttendanceData(init);
    setLocalAttendanceApt(apt);
    setLocalAttendanceOpen(true);
  };
  const saveLocalAttendance = async () => {
    const apt = localAttendanceApt;
    if (!apt) return;
    try {
      await axios.put(`${API}/appointments/${apt.id}/attendance`, { client_attendance: localAttendanceData });
      await axios.put(`${API}/appointments/${apt.id}`, { ...apt, client_attendance: localAttendanceData, status: 'completed' });
      const group = findAttGroup(apt, localAttendanceGroup);
      if (group) {
        const updatedMembers = (group.members || []).map((m) => (
          localAttendanceData[m.client_id] === 'attended'
            ? { ...m, sessions_attended: (m.sessions_attended || 0) + 1 }
            : m));
        await axios.put(`${API}/client-groups/${group.id}`, { ...group, members: updatedMembers });
      }
      if (editingAppointment?.id === apt.id) {
        setEditingAppointment?.((prev) => (prev ? { ...prev, client_attendance: localAttendanceData, status: 'completed' } : prev));
        setAppointmentFormData?.((prev) => ({ ...prev, client_attendance: localAttendanceData, status: 'completed' }));
      }
      toast.success('Attendance saved and sessions updated');
      setLocalAttendanceOpen(false);
      refreshStats?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save attendance');
    }
  };

  // ── After-hours scheduling warning (Jun 2026) ──────────────────────
  // When the tenant has configured business hours on the organization
  // and the appointment's time-of-day falls outside them, confirm (not
  // block) before saving.  Unconfigured tenants are never warned.
  const businessHours = getBusinessHours(organizations);
  const [showAfterHoursConfirm, setShowAfterHoursConfirm] = useState(false);
  const submitWithBusinessHoursCheck = (e) => {
    e.preventDefault();
    if (isOutsideBusinessHours(appointmentFormData?.start_time, appointmentFormData?.end_time, businessHours)) {
      setShowAfterHoursConfirm(true);
      return;
    }
    handleCreateAppointment(e);
  };
  const confirmAfterHoursSave = () => {
    setShowAfterHoursConfirm(false);
    handleCreateAppointment({ preventDefault: () => {} });
  };

  // ── Dialog-local state (Stage 10 part 2 — Commit 1 + 2) ────────────
  // Reschedule-response + counter-proposal flow. Only used inside this
  // dialog; previously declared in Dashboard and ferried via `legacy`.
  const [rescheduleResponseMode, setRescheduleResponseMode] = useState(null); // 'accept' | 'counter' | 'message'
  const [counterProposalDate, setCounterProposalDate] = useState('');
  const [counterProposalTime, setCounterProposalTime] = useState('');
  const [rescheduleMessage, setRescheduleMessage] = useState('');
  // Collapsible "Reminder Status" section — purely dialog-local UI state.
  const [reminderSectionExpanded, setReminderSectionExpanded] = useState(false);
  const [intakeSectionExpanded, setIntakeSectionExpanded] = useState(false);
  // Per-appointment notes cache. Keyed by appointment id to avoid stale
  // results on fast-switch. Backs the "Create Group Notes" vs "View
  // Group Notes" button logic — `notes` from NotesContext only contains
  // the currently-selected client's notes, so group appointments need
  // their per-member notes fetched separately when the dialog opens.
  const [appointmentNotesCache, setAppointmentNotesCache] = useState({
    appointmentId: null,
    notes: [],
  });
  useEffect(() => {
    if (!showAppointmentForm || !editingAppointment?.id) return;
    const apptId = editingAppointment.id;
    if (appointmentNotesCache.appointmentId === apptId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(
          `${API}/notes?appointment_id=${apptId}&limit=100&summary=true`
        );
        if (!cancelled) {
          setAppointmentNotesCache({ appointmentId: apptId, notes: resp.data || [] });
        }
      } catch (_err) {
        if (!cancelled) {
          setAppointmentNotesCache({ appointmentId: apptId, notes: [] });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showAppointmentForm, editingAppointment?.id, appointmentNotesCache.appointmentId]);
  // Optional-authorization-picker reveal flag — collapses on dialog
  // open/switch via the reset effect below.
  const [showOptionalAuthPicker, setShowOptionalAuthPicker] = useState(false);
  useEffect(() => {
    setShowOptionalAuthPicker(false);
  }, [editingAppointment?.id]);

  // ── Edit-population effect (migrated from App.js in Stage 10 part 3) ──
  // When the dialog opens in edit mode, hydrate appointmentFormData from
  // the editingAppointment. Lives here so BOTH legacy Dashboard and the
  // V2 CalendarRoute mount get edit support for free.
  useEffect(() => {
    if (editingAppointment && showAppointmentForm) {
      // Convert UTC times to local datetime-local strings
      const toLocal = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        const y = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const h = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');
        return `${y}-${mo}-${day}T${h}:${mi}`;
      };

      const appointmentCpt = editingAppointment.cpt_code_id
        ? cptCodes?.find((c) => c.id === editingAppointment.cpt_code_id)
        : null;

      // Try to infer group from client_ids if selected_group_id is missing
      let inferredGroupId =
        editingAppointment.selected_group_id || editingAppointment.group_id || '';
      if (
        !inferredGroupId &&
        editingAppointment.client_ids &&
        editingAppointment.client_ids.length > 0 &&
        clientGroups
      ) {
        const matchingGroup = clientGroups.find((group) => {
          const groupMemberIds = (group.members || []).map((m) => m.client_id);
          return editingAppointment.client_ids.every((cid) =>
            groupMemberIds.includes(cid)
          );
        });
        if (matchingGroup) inferredGroupId = matchingGroup.id;
      }

      // Recompute current group members eligible at the appointment time
      let client_ids = editingAppointment.client_ids || [];
      if (inferredGroupId) {
        const group = (clientGroups || []).find((g) => g.id === inferredGroupId);
        if (group?.members) {
          const apptDate = editingAppointment.start_time
            ? new Date(editingAppointment.start_time).toISOString().split('T')[0]
            : null;
          const eligibleMemberIds = group.members
            .filter((m) => {
              if (m.status !== 'active' && m.status !== 'removed') return false;
              if (m.start_date && apptDate) {
                const memberStart = new Date(m.start_date).toISOString().split('T')[0];
                if (memberStart > apptDate) return false;
              }
              if (m.status === 'removed' && m.exit_date && apptDate) {
                const memberExit = new Date(m.exit_date).toISOString().split('T')[0];
                if (memberExit < apptDate) return false;
              }
              return true;
            })
            .map((m) => m.client_id);
          client_ids = [...new Set([...client_ids, ...eligibleMemberIds])];
        }
      }

      setAppointmentFormData({
        client_id: editingAppointment.client_id || '',
        client_ids,
        client_attendance: editingAppointment.client_attendance || {},
        selected_group_id: inferredGroupId,
        session_number: editingAppointment.session_number || null,
        provider_id: editingAppointment.provider_id || '',
        start_time: toLocal(editingAppointment.start_time),
        end_time: toLocal(editingAppointment.end_time),
        service_type: (() => {
          if (editingAppointment.cpt_code_id) {
            const cpt = cptCodes?.find((c) => c.id === editingAppointment.cpt_code_id);
            if (cpt) return `${cpt.display_name || cpt.code} - ${cpt.description}`;
          }
          return editingAppointment.service_type || '';
        })(),
        cpt_code_id: editingAppointment.cpt_code_id || '',
        addon_cpt_code_id: editingAppointment.addon_cpt_code_id || null,
        addon_cpt_code_ids:
          Array.isArray(editingAppointment.addon_cpt_code_ids) &&
          editingAppointment.addon_cpt_code_ids.length
            ? editingAppointment.addon_cpt_code_ids
            : editingAppointment.addon_cpt_code_id
              ? [editingAppointment.addon_cpt_code_id]
              : [],
        appointment_type:
          editingAppointment.appointment_type ||
          (inferredGroupId ? 'group' : 'client_session'),
        internal_type_id: editingAppointment.internal_type_id || null,
        status: editingAppointment.status || 'scheduled',
        is_billable:
          editingAppointment.is_billable !== undefined
            ? editingAppointment.is_billable
            : true,
        is_payroll_eligible: editingAppointment.is_payroll_eligible || false,
        suppress_reminders: editingAppointment.suppress_reminders === true,
        units: editingAppointment.units || 1,
        location: editingAppointment.location || '',
        pos_code: editingAppointment.pos_code || '',
        telehealth_modality: editingAppointment.telehealth_modality || '',
        notes: editingAppointment.notes || '',
        is_recurring: false,
        recurrence_pattern: '',
        recurrence_end_date: '',
        recurrence_count: '',
        recurring_days: [],
        intake_bundle_id: editingAppointment.intake_bundle_id || '',
        // Mar 2026: surface when the bundle was last sent so the
        // Send-Intake-Bundle button can render as "✓ Sent" + gate
        // re-send behind a confirm.  See onClick handler below.
        intake_bundle_sent_at: editingAppointment.intake_bundle_sent_at || null,
        intake_bundle_sent_by: editingAppointment.intake_bundle_sent_by || null,
        invited_user_ids: editingAppointment.invited_user_ids || [],
        selectedCptMeta: appointmentCpt
          ? {
              allows_multiple_units: appointmentCpt.allows_multiple_units,
              minutes_per_unit:
                appointmentCpt.minutes_per_unit ||
                appointmentCpt.duration_minutes ||
                60,
              code: appointmentCpt.code || appointmentCpt.display_name,
              min_duration: appointmentCpt.min_duration,
              max_duration: appointmentCpt.max_duration,
            }
          : null,
      });
    }
  }, [editingAppointment, showAppointmentForm, cptCodes, clientGroups, setAppointmentFormData]);

  // Role gate — exact match to the original guard expression
  if (!(isSuperAdmin?.(user) || userRoleName === 'provider' || userRoleName === 'office' || userRoleName === 'reception' || user?.is_provider)) {
    return null;
  }

  return (
    <>
                    <Dialog open={showAppointmentForm} onOpenChange={(open) => {
                      if (!open) {
                        // Use closeAppointment to also update URL
                        closeAppointment();
                        // Reset form data
                        setAppointmentFormData({
                          client_id: '',
                          provider_id: user?.id || '',
                          start_time: '',
                          end_time: '',
                          service_type: '',
                          appointment_type: 'client_session',
                          internal_type_id: null,
                          is_billable: true,
                          is_payroll_eligible: false,
                          units: 1,
                          location: '',
                          pos_code: '',
                          telehealth_modality: '',
                          notes: '',
                          selectedCptMeta: null,
                          intake_bundle_id: '',
                          invited_user_ids: []
                        });
                        setApplicableIntakeBundles([]);
                        void 0 && console.log('Appointment form reset');
                      } else {
                        setShowAppointmentForm(true);
                      }
                    }}>
                      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <div className="flex items-center justify-between">
                            <DialogTitle>{editingAppointment ? 'Edit Appointment' : 'New Appointment'}</DialogTitle>
                            {/* Status Badge - Show dropdown when editing */}
                            {editingAppointment ? (
                              <Select 
                                value={appointmentFormData.status || 'scheduled'} 
                                onValueChange={async (value) => {
                                  // Feb 2026 — auto-save status changes from the
                                  // edit dialog (no need to hit "Save Appointment"
                                  // just to mark a checked-in / no-show / etc).
                                  // Local form state + db both updated optimistically.
                                  const prev = appointmentFormData.status || 'scheduled';
                                  setAppointmentFormData(p => ({...p, status: value}));
                                  if (editingAppointment?.id) {
                                    try {
                                      await axios.patch(`${API}/appointments/${editingAppointment.id}`, { status: value });
                                      setAppointments(prevA => prevA.map(a => a.id === editingAppointment.id ? { ...a, status: value } : a));
                                      toast.success('Status updated');
                                    } catch (err) {
                                      setAppointmentFormData(p => ({...p, status: prev}));
                                      toast.error(err?.response?.data?.detail || 'Could not update status');
                                    }
                                  }
                                }}
                              >
                                <SelectTrigger className="w-auto h-8 px-3 text-xs font-medium" data-testid="appointment-dialog-status-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="scheduled">Scheduled</SelectItem>
                                  <SelectItem value="confirmed">Confirmed</SelectItem>
                                  <SelectItem value="checked_in">Checked In</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="cancelled">Cancelled</SelectItem>
                                  <SelectItem value="no_show">No Show</SelectItem>
                                  <SelectItem value="rescheduled">Rescheduled</SelectItem>
                                  {/* Jun 2026 — payroll approval status for payroll-eligible
                                      internal types.  Set from the payroll tab; shown here so
                                      providers can see it, editable only by admins / payroll
                                      managers (backend-enforced too). */}
                                  {(selectedInternalType?.payroll_eligible || appointmentFormData.status === 'payroll_approved') && (
                                    <SelectItem value="payroll_approved" disabled={!canApprovePayroll}>
                                      Approved/Paid (Payroll)
                                    </SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge className="bg-blue-100 text-blue-700">New</Badge>
                            )}
                          </div>
                        </DialogHeader>

                        {/* ── Reschedule context banner ───────────────
                            Mar 2026 — when this dialog is opened from a
                            Reschedule task on the Tasks tab, ferry the
                            provider's structured choice + notes through
                            `appointmentFormData._reschedule_context` so
                            the scheduler doesn't have to flip between
                            tabs to know what the provider wanted.
                        */}
                        {(() => {
                          const ctx = appointmentFormData?._reschedule_context;
                          if (!ctx) return null;
                          return (
                            <div
                              className="mt-2 p-3 rounded-lg border border-indigo-300 bg-indigo-50 space-y-1.5"
                              data-testid="appointment-dialog-reschedule-context"
                            >
                              <div className="flex items-center justify-between">
                                <div className="text-xs font-semibold text-indigo-900 uppercase tracking-wide">
                                  Reschedule from task
                                </div>
                                {ctx.client_phone && (
                                  <a
                                    href={`tel:${(ctx.client_phone || '').replace(/[^\d+]/g, '')}`}
                                    className="text-xs text-teal-700 hover:underline font-medium"
                                    data-testid="appointment-dialog-reschedule-context-phone"
                                  >
                                    📞 {ctx.client_phone}
                                  </a>
                                )}
                              </div>
                              {ctx.reschedule_in_label && (
                                <div className="text-sm text-indigo-900">
                                  Provider wants reschedule in:{' '}
                                  <span className="font-semibold">{ctx.reschedule_in_label}</span>
                                  {ctx.target_appointment_date && (
                                    <span className="text-indigo-700 ml-2">
                                      · target ~{String(ctx.target_appointment_date).slice(0, 10)}
                                    </span>
                                  )}
                                </div>
                              )}
                              {ctx.source_appointment_date && (
                                <div className="text-xs text-indigo-800">
                                  Previous session:{' '}
                                  <span className="font-medium">
                                    {String(ctx.source_appointment_date).slice(0, 10)}
                                  </span>
                                </div>
                              )}
                              {ctx.description && (
                                <div className="text-xs text-indigo-900 whitespace-pre-wrap mt-1 pt-1 border-t border-indigo-200">
                                  <span className="font-semibold">Provider notes:</span>{' '}
                                  {ctx.description}
                                </div>
                              )}
                              {Array.isArray(ctx.source_note_snippet) && ctx.source_note_snippet.length > 0 && !ctx.reschedule_in_label && (ctx.action_type || '').toLowerCase() !== 'reschedule_appointment' && (
                                /* Gated out for reschedule tasks
                                   (`reschedule_in_label` set) — the
                                   structured pick + provider's
                                   description are enough; we don't
                                   want to leak unrelated note
                                   content into the scheduler's
                                   dialog. */
                                <div className="text-xs text-indigo-900 mt-1 pt-1 border-t border-indigo-200 space-y-0.5">
                                  {ctx.source_note_snippet.map((s, i) => (
                                    <div key={i}>
                                      <span className="font-semibold">{s.label}:</span> {s.value}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        
                        {/* Telehealth Join Meeting Banner - Only show when editing telehealth appointments */}
                        {editingAppointment && editingAppointment.google_meet_link && (() => {
                          // Feb 2026 — the field is named ``google_meet_link`` for
                          // historical reasons, but it now also holds the provider's
                          // ``custom_telehealth_url`` (Doxy / personal Zoom / etc.)
                          // when one is configured.  The old check insisted on the
                          // strict Google-Meet URL shape `https://meet.google.com/xxx-xxxx-xxx`,
                          // so every Doxy link was incorrectly flagged "Video link
                          // unavailable" even though it worked everywhere else
                          // (emails, calendar reminders).  Accept any well-formed
                          // http(s) URL; only the mock placeholder is treated as
                          // invalid.
                          const linkUrl = editingAppointment.google_meet_link || '';
                          const isMockLink = linkUrl.includes('mock-meeting');
                          let isWellFormedUrl = false;
                          try {
                            const parsed = new URL(linkUrl);
                            isWellFormedUrl = parsed.protocol === 'http:' || parsed.protocol === 'https:';
                          } catch {
                            isWellFormedUrl = false;
                          }
                          const hasValidLink = !isMockLink && isWellFormedUrl;
                          // Detect the link's host so we can show a friendly
                          // provider label ("Doxy.me", "Google Meet", etc.).
                          let providerLabel = 'Video meeting';
                          try {
                            const host = new URL(linkUrl).hostname.replace(/^www\./, '');
                            if (host.includes('meet.google.com')) providerLabel = 'Google Meet';
                            else if (host.includes('doxy.me')) providerLabel = 'Doxy.me';
                            else if (host.includes('zoom')) providerLabel = 'Zoom';
                            else if (host.includes('webex')) providerLabel = 'Webex';
                            else if (host.includes('teams')) providerLabel = 'Microsoft Teams';
                            else providerLabel = host;
                          } catch { /* keep default */ }
                          
                          return (
                          <div className={`border rounded-lg p-4 mb-4 ${
                            hasValidLink 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-orange-50 border-orange-200'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-full ${
                                  hasValidLink ? 'bg-green-100' : 'bg-orange-100'
                                }`}>
                                  <Video className={`h-5 w-5 ${
                                    hasValidLink ? 'text-green-600' : 'text-orange-600'
                                  }`} />
                                </div>
                                <div>
                                  <h3 className={`font-semibold ${
                                    hasValidLink ? 'text-green-900' : 'text-orange-900'
                                  }`}>Telehealth Session</h3>
                                  {hasValidLink ? (
                                    <>
                                      <p className="text-sm text-green-700">{providerLabel} is ready</p>
                                      <p className="text-xs text-gray-500 mt-1">Opens in a popup so you can take notes</p>
                                    </>
                                  ) : (
                                    <>
                                      <p className="text-sm text-orange-700">Video link unavailable</p>
                                      <p className="text-xs text-gray-500 mt-1">Cancel and recreate this appointment to generate a new link</p>
                                    </>
                                  )}
                                </div>
                              </div>
                              {hasValidLink && (
                                <div className="flex flex-col gap-2 items-stretch">
                                <Button 
                                  onClick={() => {
                                    // Open Meet in a popup window positioned in bottom-right corner
                                    openMeetPopup(editingAppointment.google_meet_link);
                                    
                                    // Clinical sessions open their note alongside the meet;
                                    // internal meetings / supervision / blocks have no notes.
                                    const isClinicalSession = ['client_session', 'group'].includes(editingAppointment.appointment_type);
                                    const appointmentNote = isClinicalSession
                                      ? notes.find(n => n.appointment_id === editingAppointment.id)
                                      : null;
                                    if (appointmentNote) {
                                      // Close the appointment dialog
                                      setShowAppointmentForm(false);
                                      setEditingAppointment(null);
                                      // Open the note as modal - stay on current tab
                                      openNote(appointmentNote);
                                      toast.success('Session started - Note opened for editing');
                                    } else if (isClinicalSession) {
                                      toast.info('Session started - No note found for this appointment');
                                    } else {
                                      toast.success('Meeting opened — check for a popup window (allow popups for this site if nothing appeared)');
                                    }
                                  }}
                                  className="bg-green-600 hover:bg-green-700 text-white font-medium shadow-sm"
                                >
                                  <Video className="h-5 w-5 mr-2" />
                                  Start Session
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="border-green-300 text-green-700 hover:bg-green-100"
                                  data-testid="copy-meet-link-btn"
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(editingAppointment.google_meet_link);
                                      toast.success('Video link copied — paste it into an email or text to the client');
                                    } catch {
                                      window.prompt('Copy the video link below:', editingAppointment.google_meet_link);
                                    }
                                  }}
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  Copy Link
                                </Button>
                                </div>
                              )}
                            </div>
                          </div>
                          );
                        })()}
                        
                        {/* Recurring Series Indicator — Feb 2026.  Surfaces
                            whether this appointment is part of a recurring
                            series (driven by `parent_appointment_id` — set
                            on every clone created via
                            ``_create_recurring_appointments``).  Helps
                            admins quickly tell a one-off from a series
                            occurrence without opening the create dialog. */}
                        {editingAppointment && editingAppointment.parent_appointment_id && (
                          <div
                            className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4 flex items-center gap-2 text-sm"
                            data-testid="recurring-series-badge"
                          >
                            <span aria-hidden="true">🔁</span>
                            <span className="font-medium text-indigo-900">Part of a recurring series</span>
                            {editingAppointment.recurrence_pattern && (
                              <span className="text-xs text-indigo-700">
                                · {editingAppointment.recurrence_pattern}
                              </span>
                            )}
                            {/* Oct 2026 — cancel this + all future occurrences
                                in the series (past/completed untouched; one
                                consolidated client email). */}
                            <button
                              type="button"
                              className="ml-auto text-xs font-medium text-red-600 border border-red-300 rounded px-2 py-1 hover:bg-red-50"
                              data-testid="cancel-future-sessions-btn"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const { data: preview } = await axios.post(
                                    `${API}/appointments/${editingAppointment.id}/cancel-future-series`,
                                    { dry_run: true },
                                  );
                                  if (!preview.count) {
                                    toast.info('No future cancellable appointments in this series.');
                                    return;
                                  }
                                  const dateList = (preview.dates || []).slice(0, 10).join('\n');
                                  const more = preview.count > 10 ? `\n…and ${preview.count - 10} more` : '';
                                  if (!window.confirm(
                                    `Cancel ${preview.count} upcoming appointment${preview.count === 1 ? '' : 's'} in this series?\n\n`
                                    + `${dateList}${more}\n\n`
                                    + 'Past and completed sessions are not affected. '
                                    + 'The client will receive ONE email listing all cancelled dates.\n\nProceed?'
                                  )) return;
                                  const { data } = await axios.post(
                                    `${API}/appointments/${editingAppointment.id}/cancel-future-series`,
                                    {},
                                  );
                                  toast.success(`${data.message}${data.email_sent ? ' — client notified' : ''}`);
                                  if (typeof fetchAppointments === 'function') fetchAppointments();
                                  setShowAppointmentForm(false);
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || 'Failed to cancel future sessions');
                                }
                              }}
                            >
                              Cancel Future Sessions
                            </button>
                          </div>
                        )}

                        
                        {/* Authorization picker — CREATE mode only.  For
                            EDIT mode, the richer "Authorization Status"
                            section below handles linking / unlinking
                            against the persisted appointment. */}
                        {!editingAppointment && appointmentFormData.client_id && (isSuperAdmin(user) || user?.is_billing_manager || ['office','biller','reception','admin'].includes((user?.role || '').toLowerCase()) || user?.is_scheduler) && (() => {
                          const selectedCpt = cptCodes?.find((c) => c.id === appointmentFormData.cpt_code_id);
                          // Show the picker when the CPT either REQUIRES
                          // auth or ALLOWS auth.  If neither flag is set,
                          // skip the section entirely.
                          const showPicker = selectedCpt && (selectedCpt.requires_authorization || selectedCpt.allow_authorization);
                          if (!showPicker) return null;
                          const isOptional = !!selectedCpt.allow_authorization && !selectedCpt.requires_authorization;
                          const eligible = ((clientAuthorizations || []).concat(allAuthorizations || []))
                            .filter((auth, idx, arr) =>
                              arr.findIndex((a) => a.id === auth.id) === idx
                              && auth.client_id === appointmentFormData.client_id
                              && auth.is_active
                              && (auth.remaining_units || 0) > 0,
                            );
                          return (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                              <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                                <Shield className="h-5 w-5" />
                                Authorization {isOptional ? '(optional)' : '(required)'}
                              </h3>
                              <p className="text-xs text-gray-600 mb-2">
                                {isOptional
                                  ? 'This CPT allows but does not require an auth. Pick one to attach, or leave on Auto-detect to skip.'
                                  : 'Pick an auth to attach when the appointment is created. Auto-detect tries to match by CPT.'}
                              </p>
                              <Select
                                value={appointmentFormData.authorization_id || '_auto'}
                                onValueChange={(value) => setAppointmentFormData({
                                  ...appointmentFormData,
                                  authorization_id: value === '_auto' ? null : value,
                                })}
                              >
                                <SelectTrigger data-testid="create-auth-picker" className="bg-white">
                                  <SelectValue placeholder="Auto-detect by CPT" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_auto">Auto-detect by CPT</SelectItem>
                                  {eligible.length === 0 ? (
                                    <SelectItem value="_none" disabled>
                                      No active authorizations with available units
                                    </SelectItem>
                                  ) : (
                                    eligible.map((auth) => {
                                      const endDate = auth.end_date ? new Date(auth.end_date).toLocaleDateString() : '';
                                      const stMatch = (serviceTypes || []).find(st => st.id === auth.service_type);
                                      const serviceLabel = stMatch?.name || auth.service_type || 'Unknown service';
                                      return (
                                        <SelectItem key={auth.id} value={auth.id}>
                                          {auth.authorization_number} — {serviceLabel} · {auth.remaining_units}/{auth.total_units} units remaining{endDate ? ` · expires ${endDate}` : ''}
                                        </SelectItem>
                                      );
                                    })
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        })()}

                        {/* Authorization Link Section - Only show when editing appointments.
                            Feb 2026: three CPT modes:
                              • requires_authorization → full picker (auth REQUIRED)
                              • allow_authorization (only)  → "not required, link anyway?" affordance (auth OPTIONAL)
                              • neither → DON'T render the panel — no auth applies at all */}
                        {editingAppointment && (() => {
                          // Check if the CPT code requires authorization
                          const appointmentCptCode = cptCodes?.find(c => c.id === editingAppointment.cpt_code_id);
                          const requiresAuth = appointmentCptCode?.requires_authorization;
                          const allowsAuth = appointmentCptCode?.allow_authorization;

                          // If this CPT supports neither auth mode AND no auth is linked,
                          // hide the panel entirely — those services don't track auths.
                          if (!requiresAuth && !allowsAuth && !editingAppointment.authorization_id) {
                            return null;
                          }

                          // Optional manual-link mode: CPT allows but doesn't require auth,
                          // and none is linked.  Surface a soft hint + an opt-in button to
                          // reveal the full picker.
                          if (!requiresAuth && allowsAuth && !editingAppointment.authorization_id) {
                            if (!showOptionalAuthPicker) {
                              return (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                                  <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-2 text-gray-600">
                                      <Shield className="h-5 w-5" />
                                      <span className="text-sm">Authorization optional for this service</span>
                                    </div>
                                    {(isSuperAdmin(user) || user?.is_billing_manager || ['office','biller','reception','admin'].includes((user?.role || '').toLowerCase()) || user?.is_scheduler) && (
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        data-testid="link-optional-auth-btn"
                                        onClick={() => setShowOptionalAuthPicker(true)}
                                      >
                                        + Link an authorization
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            // Fall through to render the full picker UI below.
                          }
                          
                          // Otherwise show full authorization status UI
                          return (
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                            <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                              <Shield className="h-5 w-5" />
                              Authorization Status
                            </h3>
                            {editingAppointment.authorization_id ? (
                              <div className="space-y-3">
                                <div className="bg-white rounded-lg p-3 border border-purple-100">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      {(() => {
                                        // Fall back to looking the auth doc up
                                        // by id when the appointment's
                                        // `authorization_number` is null / undefined.
                                        // Prevents the UI from rendering a UUID
                                        // in place of the human-readable number
                                        // (Feb 2026 user report).
                                        const lookedUp = ((clientAuthorizations || []).concat(allAuthorizations || []))
                                          .find((a) => a.id === editingAppointment.authorization_id);
                                        const displayNumber = editingAppointment.authorization_number
                                          || lookedUp?.authorization_number
                                          || `(auth id ${editingAppointment.authorization_id.slice(0, 8)}…)`;
                                        const expiresOn = lookedUp?.end_date
                                          ? new Date(lookedUp.end_date).toLocaleDateString()
                                          : null;
                                        return (
                                          <>
                                            <p className="text-sm font-medium text-purple-900">
                                              Linked to Auth: <span className="font-mono">{displayNumber}</span>
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                              Units deducted: {editingAppointment.authorization_units_deducted || 1}
                                              {lookedUp ? ` · ${lookedUp.remaining_units}/${lookedUp.total_units} remaining` : ''}
                                              {expiresOn ? ` · expires ${expiresOn}` : ''}
                                            </p>
                                          </>
                                        );
                                      })()}
                                    </div>
                                    {(isSuperAdmin(user) || user?.is_billing_manager || ['office','biller','reception','admin'].includes((user?.role || '').toLowerCase()) || user?.is_scheduler) && (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        onClick={async () => {
                                          if (window.confirm('Unlink this appointment from its authorization? This will restore the deducted units.')) {
                                            try {
                                              const response = await axios.delete(`${API}/appointments/${editingAppointment.id}/unlink-authorization`);
                                              toast.success(response.data.message);
                                              // Update local state
                                              setEditingAppointment(prev => ({
                                                ...prev,
                                                authorization_id: null,
                                                authorization_number: null,
                                                authorization_units_deducted: null
                                              }));
                                              fetchAppointments();
                                            } catch (err) {
                                              toast.error(err.response?.data?.detail || 'Failed to unlink authorization');
                                            }
                                          }
                                        }}
                                      >
                                        Unlink
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <p className="text-sm text-purple-700">No authorization linked to this appointment.</p>
                                {(isSuperAdmin(user) || user?.is_billing_manager || ['office','biller','reception','admin'].includes((user?.role || '').toLowerCase()) || user?.is_scheduler) && editingAppointment.client_id && (
                                  <div className="space-y-2">
                                    <Label className="text-sm text-purple-900">Link to Authorization</Label>
                                    <Select
                                      value=""
                                      onValueChange={async (authId) => {
                                        if (authId && authId !== '_no_auths') {
                                          try {
                                            const response = await axios.post(`${API}/appointments/${editingAppointment.id}/link-authorization/${authId}`);
                                            toast.success(response.data.message);
                                            // Update local state
                                            setEditingAppointment(prev => ({
                                              ...prev,
                                              authorization_id: authId,
                                              authorization_number: response.data.auth_number || authId,
                                              authorization_units_deducted: response.data.units_deducted
                                            }));
                                            fetchAppointments();
                                          } catch (err) {
                                            toast.error(err.response?.data?.detail || 'Failed to link authorization');
                                          }
                                        }
                                      }}
                                    >
                                      <SelectTrigger className="bg-white">
                                        <SelectValue placeholder="Select authorization to link..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {((clientAuthorizations || []).concat(allAuthorizations || []))
                                          .filter((auth, idx, arr) =>
                                            // Dedupe by id (client + global lists overlap)
                                            arr.findIndex((a) => a.id === auth.id) === idx
                                            && auth.client_id === editingAppointment.client_id
                                            && auth.is_active
                                            && (auth.remaining_units || 0) > 0
                                          )
                                          .map(auth => {
                                            const endDate = auth.end_date ? new Date(auth.end_date).toLocaleDateString() : '';
                                            // Resolve service_type (UUID) to a human-readable
                                            // name.  Stored value is the service-type id; if
                                            // we can't find a match (legacy auths, missing
                                            // serviceTypes load), fall back to the raw value
                                            // so the row is never blank.
                                            const stMatch = (serviceTypes || []).find(st => st.id === auth.service_type);
                                            const serviceLabel = stMatch?.name || auth.service_type || 'Unknown service';
                                            return (
                                              <SelectItem key={auth.id} value={auth.id}>
                                                {auth.authorization_number} — {serviceLabel} · {auth.remaining_units}/{auth.total_units} units remaining{endDate ? ` · expires ${endDate}` : ''}
                                              </SelectItem>
                                            );
                                          })
                                        }
                                        {(() => {
                                          const merged = ((clientAuthorizations || []).concat(allAuthorizations || []))
                                            .filter((auth, idx, arr) =>
                                              arr.findIndex((a) => a.id === auth.id) === idx
                                              && auth.client_id === editingAppointment.client_id
                                              && auth.is_active
                                              && (auth.remaining_units || 0) > 0
                                            );
                                          return merged.length === 0 ? (
                                            <SelectItem value="_no_auths" disabled>
                                              No active authorizations with available units
                                            </SelectItem>
                                          ) : null;
                                        })()}
                                      </SelectContent>
                                    </Select>
                                    <p className="text-xs text-gray-500">
                                      Picks from the Authorization Management list — no need to open the chart first.
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          );
                        })()}
                        
                        {/* Reschedule Response UI - Only show for reschedule_requested appointments */}
                        {editingAppointment?.status === 'reschedule_requested' && (() => {
                          void 0 && console.log('Reschedule dialog - editingAppointment:', editingAppointment);
                          void 0 && console.log('proposed_date:', editingAppointment.proposed_date);
                          void 0 && console.log('proposed_time:', editingAppointment.proposed_time);
                          return true;
                        })() && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                            <h3 className="font-semibold text-yellow-900 mb-3">🔔 Reschedule Request</h3>
                            
                            {/* Appointment Details */}
                            <div className="bg-white border rounded p-3 mb-3 space-y-2 text-sm">
                              <div className="grid grid-cols-2 gap-2">
                                <div><strong>Client:</strong> {editingAppointment.client_name || 'N/A'}</div>
                                <div><strong>Provider:</strong> {editingAppointment.provider_name || 'N/A'}</div>
                                <div><strong>Service:</strong> {editingAppointment.service_type || 'N/A'}</div>
                                <div><strong>Status:</strong> <span className="text-yellow-700 font-semibold">Reschedule Requested</span></div>
                              </div>
                            </div>
                            
                            {/* Current vs Requested Times */}
                            <div className="space-y-2 text-sm mb-3">
                              <p className="font-medium text-gray-700">Time Change:</p>
                              <div className="bg-white p-2 rounded border">
                                <p><strong>Current:</strong> {new Date(editingAppointment.start_time).toLocaleString('en-US', {month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'})}</p>
                              </div>
                              <div className="bg-blue-50 p-2 rounded border border-blue-200">
                                <p><strong>Client Requested:</strong> {editingAppointment.proposed_date || 'N/A'} at {editingAppointment.proposed_time || 'N/A'}</p>
                                {!editingAppointment.proposed_date && !editingAppointment.proposed_time && (
                                  <p className="text-xs text-red-600 mt-1">⚠️ No proposed time found in data</p>
                                )}
                              </div>
                              {editingAppointment.reschedule_note && (
                                <div className="bg-white p-2 rounded border">
                                  <p><strong>Client Note:</strong> "{editingAppointment.reschedule_note}"</p>
                                </div>
                              )}
                            </div>
                            
                            <div className="mt-4 space-y-3">
                              {/* Accept Client's Time */}
                              <Button
                                type="button"
                                className="w-full bg-green-600 hover:bg-green-700"
                                disabled={!editingAppointment.proposed_date || !editingAppointment.proposed_time}
                                onClick={async () => {
                                    try {
                                      await axios.post(`${API}/appointments/${editingAppointment.id}/reschedule/accept`);
                                      toast.success('Reschedule request accepted');
                                      setShowAppointmentForm(false);
                                      fetchAppointments();
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Failed to accept reschedule');
                                    }
                                  }}
                                >
                                  Accept Client's Requested Time
                                </Button>
                              
                              {/* Counter-Propose */}
                              {rescheduleResponseMode !== 'counter' ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="w-full"
                                  onClick={() => setRescheduleResponseMode('counter')}
                                >
                                  Propose Different Time
                                </Button>
                              ) : (
                                <div className="border rounded p-3 space-y-3">
                                  <h4 className="font-medium text-sm">Propose Different Time:</h4>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <Label className="text-xs">Date (MM/DD/YYYY)</Label>
                                      <input
                                        type="text"
                                        placeholder="MM/DD/YYYY"
                                        className="w-full border rounded p-2 text-sm"
                                        value={counterProposalDate}
                                        onChange={(e) => setCounterProposalDate(e.target.value)}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Time</Label>
                                      <input
                                        type="text"
                                        placeholder="2:00 PM"
                                        className="w-full border rounded p-2 text-sm"
                                        value={counterProposalTime}
                                        onChange={(e) => setCounterProposalTime(e.target.value)}
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <Label className="text-xs">Message (optional)</Label>
                                    <DebouncedTextarea
                                      className="w-full border rounded p-2 text-sm"
                                      rows={2}
                                      placeholder="Let the client know why this time works better..."
                                      value={rescheduleMessage}
                                      onChange={(e) => setRescheduleMessage(e.target.value)}
                                    />
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      type="button"
                                      size="sm"
                                      className="flex-1"
                                      onClick={async () => {
                                        if (!counterProposalDate || !counterProposalTime) {
                                          toast.error('Please enter date and time');
                                          return;
                                        }
                                        try {
                                          await axios.post(`${API}/appointments/${editingAppointment.id}/reschedule/counter-propose`, null, {
                                            params: {
                                              proposed_date: counterProposalDate,
                                              proposed_time: counterProposalTime,
                                              message: rescheduleMessage || null
                                            }
                                          });
                                          toast.success('Counter-proposal sent to client');
                                          setShowAppointmentForm(false);
                                          setRescheduleResponseMode(null);
                                          setCounterProposalDate('');
                                          setCounterProposalTime('');
                                          setRescheduleMessage('');
                                          fetchAppointments();
                                        } catch (err) {
                                          toast.error(err.response?.data?.detail || 'Failed to send counter-proposal');
                                        }
                                      }}
                                    >
                                      Send Proposal
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setRescheduleResponseMode(null);
                                        setCounterProposalDate('');
                                        setCounterProposalTime('');
                                        setRescheduleMessage('');
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              )}
                              
                              {/* Send Message Only */}
                              {rescheduleResponseMode !== 'message' ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="w-full"
                                  onClick={() => setRescheduleResponseMode('message')}
                                >
                                  💬 Send Message to Client
                                </Button>
                              ) : (
                                <div className="border rounded p-3 space-y-3">
                                  <h4 className="font-medium text-sm">Send Message:</h4>
                                  <DebouncedTextarea
                                    className="w-full border rounded p-2 text-sm"
                                    rows={3}
                                    placeholder="E.g., 'I'll call you to discuss scheduling options'"
                                    value={rescheduleMessage}
                                    onChange={(e) => setRescheduleMessage(e.target.value)}
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      type="button"
                                      size="sm"
                                      className="flex-1"
                                      onClick={async () => {
                                        if (!rescheduleMessage) {
                                          toast.error('Please enter a message');
                                          return;
                                        }
                                        try {
                                          await axios.post(`${API}/appointments/${editingAppointment.id}/reschedule/message`, null, {
                                            params: { message: rescheduleMessage }
                                          });
                                          toast.success('Message sent to client');
                                          setShowAppointmentForm(false);
                                          setRescheduleResponseMode(null);
                                          setRescheduleMessage('');
                                        } catch (err) {
                                          toast.error(err.response?.data?.detail || 'Failed to send message');
                                        }
                                      }}
                                    >
                                      Send Message
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setRescheduleResponseMode(null);
                                        setRescheduleMessage('');
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Hide regular form for reschedule requests - only show response options above */}
                        {editingAppointment?.status !== 'reschedule_requested' && (
                        <form onSubmit={submitWithBusinessHoursCheck} className="space-y-3">
                          {editingAppointment?.payroll_ineligible && (
                            <div
                              className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-gray-100 border border-gray-200 text-xs text-gray-600"
                              data-testid="appointment-payroll-ineligible-marker"
                            >
                              <span className="font-semibold text-gray-700">Not payroll-eligible</span>
                              {editingAppointment.payroll_ineligible_reason && (
                                <span className="italic">— {editingAppointment.payroll_ineligible_reason}</span>
                              )}
                            </div>
                          )}
                          {/* Row 1: Appointment Type | Provider */}
                          <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="appointment_type" className="text-sm">Appointment Type</Label>
                            <Select 
                              value={internalTypeSelectValue} 
                              onValueChange={(value) => {
                                // Radix fires a spurious onValueChange('') when the
                                // selected item briefly unmounts (e.g. while the
                                // managed-types list loads) — never wipe the form.
                                if (!value) return;
                                if (value.startsWith('itype:')) {
                                  const t = internalTypes.find((x) => `itype:${x.id}` === value);
                                  setAppointmentFormData(prev => {
                                    const next = {
                                      ...prev,
                                      appointment_type: t?.slug === 'supervision' ? 'supervision' : 'internal',
                                      internal_type_id: t?.id || null,
                                      client_id: '',
                                      is_billable: false,
                                      is_payroll_eligible: !!(t?.payroll_eligible && t?.payroll_code),
                                    };
                                    // Default duration: shift end_time from start_time.
                                    const mins = parseInt(t?.default_duration_minutes, 10);
                                    if (mins > 0 && prev.start_time) {
                                      const st = new Date(prev.start_time);
                                      if (!Number.isNaN(st.getTime())) {
                                        const et = new Date(st.getTime() + mins * 60000);
                                        const pad = (n) => String(n).padStart(2, '0');
                                        next.end_time = `${et.getFullYear()}-${pad(et.getMonth() + 1)}-${pad(et.getDate())}T${pad(et.getHours())}:${pad(et.getMinutes())}`;
                                      }
                                    }
                                    return next;
                                  });
                                  return;
                                }
                                setAppointmentFormData(prev => ({
                                  ...prev, 
                                  appointment_type: value,
                                  internal_type_id: null,
                                  client_id: value !== 'client_session' ? '' : prev.client_id,
                                  is_billable: value === 'client_session',
                                  is_payroll_eligible: false
                                }));
                              }}
                            >
                              <SelectTrigger data-testid="appointment-type" className="h-9">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="client_session">Client Session (Billable)</SelectItem>
                                <SelectItem value="group">Group Session</SelectItem>
                                <SelectItem value="block_time">Personal Time</SelectItem>
                                {internalTypes
                                  .filter((t) => t.is_active !== false || t.id === appointmentFormData.internal_type_id)
                                  .map((t) => (
                                  <SelectItem key={t.id} value={`itype:${t.id}`}>
                                    {t.name}
                                    {t.is_active === false
                                      ? ' (inactive)'
                                      : (t.payroll_eligible && t.payroll_code ? ' (Payable)' : '')}
                                  </SelectItem>
                                ))}
                                {/* Literal legacy values — only rendered when no
                                    managed type owns the slug, so the dropdown
                                    never shows duplicate Supervision / Internal
                                    Meeting entries. */}
                                {!internalTypes.some((t) => t.slug === 'supervision') && (
                                  <SelectItem value="supervision">Supervision</SelectItem>
                                )}
                                {!internalTypes.some((t) => t.slug === 'internal') && (
                                  <SelectItem value="internal">Internal Meeting</SelectItem>
                                )}
                                <SelectItem value="vacation">Vacation Day</SelectItem>
                                <SelectItem value="sick_day">Sick Day</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label htmlFor="appointment_provider" className="text-sm">
                              {appointmentFormData.appointment_type === 'client_session' || appointmentFormData.appointment_type === 'group' 
                                ? 'Provider *' 
                                : 'Organized By *'}
                            </Label>
                            {/* Show read-only text for invitees */}
                            {editingAppointment && editingAppointment.invited_user_ids?.includes(user?.id) && editingAppointment.provider_id !== user?.id ? (
                              <div>
                                <div className="flex items-center h-9 px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-sm">
                                  {(() => {
                                    const organizer = users?.find(u => u.id === editingAppointment.provider_id);
                                    return organizer 
                                      ? `${organizer.full_name}${organizer.credentials ? `, ${organizer.credentials}` : ''}`
                                      : 'Unknown';
                                  })()}
                                </div>
                              </div>
                            ) : (
                            <Select 
                              value={appointmentFormData.provider_id} 
                              onValueChange={(value) => {
                                const selectedProvider = providers?.find(p => p.id === value) || users?.find(u => u.id === value) || archivedProviders.find(p => p.id === value);
                                const updates = { provider_id: value };
                                if (selectedProvider?.default_location) {
                                  updates.location = selectedProvider.default_location;
                                }
                                if (selectedProvider?.default_pos_code) {
                                  updates.pos_code = selectedProvider.default_pos_code;
                                }
                                setAppointmentFormData(prev => ({...prev, ...updates}));
                              }}
                              required
                            >
                              <SelectTrigger data-testid="appointment-provider" className="h-9">
                                <SelectValue placeholder="Select provider" />
                              </SelectTrigger>
                              <SelectContent>
                                <ArchivedProvidersToggleRow includeArchived={includeArchived} onToggle={toggleArchived} />
                                {(() => {
                                  try {
                                    const accessibleProviders = mergeProviders(getAccessibleProviders(appointmentFormData.appointment_type))
                                      .slice()
                                      .sort((a, b) =>
                                        ((a.is_archived ? 1 : 0) - (b.is_archived ? 1 : 0))
                                        || (a.full_name || '').localeCompare(b.full_name || ''));
                                    if (!accessibleProviders || accessibleProviders.length === 0) {
                                      return <SelectItem value="_none" disabled>No providers available</SelectItem>;
                                    }
                                    return accessibleProviders.map(provider => (
                                      <SelectItem key={provider.id} value={provider.id} className={provider.is_archived ? 'text-gray-400' : undefined}>
                                        <span className="flex items-center gap-2">
                                          {provider.full_name}{provider.credentials ? `, ${provider.credentials}` : ''}
                                          {provider.is_archived && <ArchivedProviderTag />}
                                        </span>
                                      </SelectItem>
                                    ));
                                  } catch (error) {
                                    return <SelectItem value="_error" disabled>Error loading providers</SelectItem>;
                                  }
                                })()}
                              </SelectContent>
                            </Select>
                            )}
                          </div>
                          </div>

                          {/* Invitation Response - Show for invited users viewing an existing appointment */}
                          {editingAppointment && 
                           editingAppointment.invited_user_ids?.includes(user?.id) && 
                           editingAppointment.provider_id !== user?.id && (
                            <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="font-medium text-blue-900">📩 You're Invited</h4>
                                  <p className="text-sm text-blue-700">
                                    {users?.find(u => u.id === editingAppointment.provider_id)?.full_name || 'Someone'} invited you to this {
                                      editingAppointment.appointment_type === 'block_time' ? 'personal time' :
                                      editingAppointment.appointment_type === 'supervision' ? 'supervision session' :
                                      editingAppointment.appointment_type === 'internal' ? 'internal meeting' :
                                      'appointment'
                                    }
                                  </p>
                                  {editingAppointment.invitation_responses?.[user?.id] && (
                                    <p className="text-sm mt-1">
                                      <span className={`font-medium ${
                                        editingAppointment.invitation_responses[user.id] === 'accepted' ? 'text-green-700' : 'text-red-700'
                                      }`}>
                                        You {editingAppointment.invitation_responses[user.id]} this invitation
                                      </span>
                                    </p>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant={editingAppointment.invitation_responses?.[user?.id] === 'accepted' ? 'default' : 'outline'}
                                    className={editingAppointment.invitation_responses?.[user?.id] === 'accepted' ? 'bg-green-600 hover:bg-green-700' : ''}
                                    onClick={async () => {
                                      try {
                                        // Include on_behalf_of for "View As" feature
                                        const onBehalfOf = impersonatedUserId ? `&on_behalf_of=${impersonatedUserId}` : '';
                                        await axios.post(`${API}/appointments/${editingAppointment.id}/respond-invitation?response=accepted${onBehalfOf}`);
                                        toast.success('Invitation accepted');
                                        fetchAppointments();
                                        // Update local state - use the effective user ID
                                        const effectiveUserId = impersonatedUserId || user.id;
                                        setEditingAppointment(prev => ({
                                          ...prev,
                                          invitation_responses: {
                                            ...prev.invitation_responses,
                                            [effectiveUserId]: 'accepted'
                                          }
                                        }));
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Failed to respond');
                                      }
                                    }}
                                  >
                                    ✓ Accept
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant={editingAppointment.invitation_responses?.[user?.id] === 'declined' ? 'default' : 'outline'}
                                    className={editingAppointment.invitation_responses?.[user?.id] === 'declined' ? 'bg-red-600 hover:bg-red-700' : ''}
                                    onClick={async () => {
                                      try {
                                        // Include on_behalf_of for "View As" feature
                                        const onBehalfOf = impersonatedUserId ? `&on_behalf_of=${impersonatedUserId}` : '';
                                        await axios.post(`${API}/appointments/${editingAppointment.id}/respond-invitation?response=declined${onBehalfOf}`);
                                        toast.success('Invitation declined');
                                        fetchAppointments();
                                        // Update local state - use the effective user ID
                                        const effectiveUserId = impersonatedUserId || user.id;
                                        setEditingAppointment(prev => ({
                                          ...prev,
                                          invitation_responses: {
                                            ...prev.invitation_responses,
                                            [effectiveUserId]: 'declined'
                                          }
                                        }));
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Failed to respond');
                                      }
                                    }}
                                  >
                                    ✗ Decline
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Invite Users - For internal meetings, supervision, personal time */}
                          {['block_time', 'supervision', 'internal'].includes(appointmentFormData.appointment_type) && (
                            <div className="col-span-2">
                              <Label>Invite Other Users</Label>
                              <div className="mt-2 space-y-2">
                                {/* Selected users with response status */}
                                {(appointmentFormData.invited_user_ids?.length > 0) && (
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    {(appointmentFormData.invited_user_ids || []).map(userId => {
                                      const invitedUser = users?.find(u => u.id === userId);
                                      // Get response status from editingAppointment (if editing) or default to pending
                                      const responseStatus = editingAppointment?.invitation_responses?.[userId];
                                      
                                      // Determine badge color based on response
                                      let badgeClass = 'bg-gray-100 text-gray-700 border border-gray-300'; // Pending
                                      let statusIcon = '⏳';
                                      let statusText = 'Pending';
                                      
                                      if (responseStatus === 'accepted') {
                                        badgeClass = 'bg-green-100 text-green-800 border border-green-300';
                                        statusIcon = '✓';
                                        statusText = 'Accepted';
                                      } else if (responseStatus === 'declined') {
                                        badgeClass = 'bg-red-100 text-red-800 border border-red-300';
                                        statusIcon = '✗';
                                        statusText = 'Declined';
                                      }
                                      
                                      return (
                                        <span 
                                          key={userId}
                                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${badgeClass}`}
                                          title={statusText}
                                        >
                                          <span className="text-xs">{statusIcon}</span>
                                          {invitedUser?.full_name || 'Unknown User'}
                                          {/* Only show remove button if user is the organizer */}
                                          {(!editingAppointment || editingAppointment.provider_id === user?.id) && (
                                            <button
                                              type="button"
                                              onClick={() => setAppointmentFormData(prev => ({
                                                ...prev,
                                                invited_user_ids: prev.invited_user_ids.filter(id => id !== userId)
                                              }))}
                                              className="ml-1 hover:text-red-600"
                                            >
                                              ×
                                            </button>
                                          )}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                                {/* User selector - only show for organizer */}
                                {(!editingAppointment || editingAppointment.provider_id === user?.id) && (
                                <Select
                                  value=""
                                  onValueChange={(userId) => {
                                    void 0 && console.log('Adding invited user:', userId);
                                    void 0 && console.log('Current invited_user_ids:', appointmentFormData.invited_user_ids);
                                    if (userId && !appointmentFormData.invited_user_ids?.includes(userId)) {
                                      setAppointmentFormData(prev => {
                                        const updated = {
                                          ...prev,
                                          invited_user_ids: [...(prev.invited_user_ids || []), userId]
                                        };
                                        void 0 && console.log('Updated appointmentFormData:', updated.invited_user_ids);
                                        return updated;
                                      });
                                    }
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Add user to invite..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(users || [])
                                      .filter(u => 
                                        u.id !== appointmentFormData.provider_id && 
                                        !appointmentFormData.invited_user_ids?.includes(u.id) &&
                                        u.status !== 'inactive'
                                      )
                                      .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
                                      .map(u => (
                                        <SelectItem key={u.id} value={u.id}>
                                          {u.full_name} {u.credentials ? `(${u.credentials})` : ''}
                                        </SelectItem>
                                      ))
                                    }
                                  </SelectContent>
                                </Select>
                                )}
                                <p className="text-xs text-gray-500">
                                  {editingAppointment ? (
                                    <>
                                      <span className="inline-flex items-center gap-1"><span className="text-green-600">✓</span> Accepted</span>
                                      <span className="mx-2">|</span>
                                      <span className="inline-flex items-center gap-1"><span className="text-red-600">✗</span> Declined</span>
                                      <span className="mx-2">|</span>
                                      <span className="inline-flex items-center gap-1"><span className="text-gray-500">⏳</span> Pending</span>
                                    </>
                                  ) : 'Invited users will see this on their calendar and can accept or decline'}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Row 2: Client | Service Type - 2-column grid for client sessions */}
                          {appointmentFormData.appointment_type === 'client_session' && !appointmentFormData.selectedCptMeta?.is_group_session && (
                            <div className="grid grid-cols-2 gap-3">
                              {/* Client Selection */}
                              <div>
                                <Label htmlFor="client_id" className="text-sm">Client *</Label>
                                {/* Show eligibility status inline when a client is selected */}
                                {appointmentFormData.client_id && (() => {
                                  const formClient = allClientsForLookup?.find(c => c.id === appointmentFormData.client_id) || clients?.find(c => c.id === appointmentFormData.client_id);
                                  if (!formClient) return null;
                                  const status = formClient.primary_eligibility_status || 'not_checked';
                                  const dos = appointmentFormData.start_time ? appointmentFormData.start_time.substring(0, 10) : null;
                                  const { icon, color, label } = dos ? getDOSEligibilityDisplay(formClient, dos) : getEligibilityDisplay(status);
                                  const formDetail = selectedClient?.id === formClient?.id ? safeEligibilityDetail : null;
                                  const tooltip = eligibilityTooltip(formClient, formDetail);
                                  const isStale = formClient.last_eligibility_check
                                    ? (Date.now() - new Date(formClient.last_eligibility_check).getTime()) > 24 * 3600 * 1000
                                    : true;
                                  return (
                                    <span className="inline-flex items-center gap-1 ml-2">
                                      <span className={`text-xs font-semibold ${color}`} title={tooltip}>
                                        {icon} {label}
                                      </span>
                                      <button
                                        type="button"
                                        className="text-[10px] text-blue-500 underline hover:text-blue-700 ml-1"
                                        title={isStale ? 'Eligibility may be stale — click to re-check' : 'Re-check eligibility for this client'}
                                        onClick={() => handleEligibilityCheck(formClient.id, formClient.full_name, { dateOfService: dos || undefined })}
                                      >
                                        {isStale ? '↻ Refresh' : '↻'}
                                      </button>
                                    </span>
                                  );
                                })()}
                                <Select
                                  value={appointmentFormData.client_id}
                                  onValueChange={(value) => setAppointmentFormData(prev => ({...prev, client_id: value}))}
                                >
                                  <SelectTrigger data-testid="appointment-client" className="h-9">
                                    <SelectValue placeholder="Select client" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getAccessibleClients(appointmentFormData.provider_id).length === 0 ? (
                                      <div className="px-2 py-4 text-sm text-gray-500 text-center">
                                        {allClientsForLookup?.length === 0 
                                          ? "Loading clients..." 
                                          : "No clients assigned to this provider"}
                                      </div>
                                    ) : (
                                      getAccessibleClients(appointmentFormData.provider_id).map(client => (
                                        <SelectItem key={client.id} value={client.id}>
                                          {formatClientName(client)}
                                        </SelectItem>
                                      ))
                                    )}
                                  </SelectContent>
                                </Select>
                                {/* Mar 2026 — Show the selected client's
                                    phone right under the picker so the
                                    scheduler can dial without leaving
                                    the dialog (saves a chart hop when
                                    confirming / rescheduling).  The
                                    "Open Chart" link sits alongside —
                                    matches the pattern used on
                                    Authorizations Mgmt + the Tasks
                                    panel for one-click context. */}
                                {appointmentFormData.client_id && (() => {
                                  const selected = allClientsForLookup?.find(c => c.id === appointmentFormData.client_id)
                                    || clients?.find(c => c.id === appointmentFormData.client_id);
                                  if (!selected) return null;
                                  const phone = selected.phone || selected.reminder_sms_phone;
                                  return (
                                    <div
                                      className="mt-1 text-xs text-gray-600 flex items-center gap-3 flex-wrap"
                                      data-testid="appointment-client-quick-actions"
                                    >
                                      {/* Jun 2026 user request: DOB to the
                                          left of the phone number. */}
                                      {selected.date_of_birth && (
                                        <span
                                          className="flex items-center gap-1.5"
                                          data-testid="appointment-client-dob"
                                          title="Date of birth"
                                        >
                                          <span aria-hidden="true">🎂</span>
                                          {(() => {
                                            const s = String(selected.date_of_birth).slice(0, 10);
                                            const [y, m, d] = s.split('-');
                                            return (y && m && d) ? `${m}/${d}/${y}` : s;
                                          })()}
                                        </span>
                                      )}
                                      {phone && (
                                        <span
                                          className="flex items-center gap-1.5"
                                          data-testid="appointment-client-phone"
                                        >
                                          <span aria-hidden="true">📞</span>
                                          <a
                                            href={`tel:${String(phone).replace(/[^+\d]/g, '')}`}
                                            className="hover:text-teal-700 hover:underline"
                                            title="Click to dial"
                                          >
                                            {phone}
                                          </a>
                                        </span>
                                      )}
                                      <button
                                        type="button"
                                        className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-800 hover:underline"
                                        onClick={() => {
                                          // Close the dialog before navigating so
                                          // the user isn't dropped onto the chart
                                          // with a stale modal still mounted.
                                          if (typeof closeAppointment === 'function') {
                                            closeAppointment();
                                          } else {
                                            setShowAppointmentForm?.(false);
                                          }
                                          // Jun 2026 — stay in the V2 shell when the
                                          // calendar/appointment lives there; only the
                                          // legacy dashboard goes to the legacy chart.
                                          const inV2 = window.location.pathname.startsWith('/v2');
                                          navigate(
                                            inV2 ? `/v2/clients/${selected.id}` : `/client/${selected.id}`,
                                            inV2 ? { state: { from: window.location.pathname + window.location.search } } : undefined
                                          );
                                        }}
                                        title="Open the client's chart in a new view"
                                        data-testid="appointment-open-client-chart"
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                        Open Chart
                                      </button>
                                    </div>
                                  );
                                })()}
                              </div>
                              
                              {/* Service Type/CPT Code */}
                              <div>
                                <Label htmlFor="service_type" className="text-sm">Service Type (CPT) *</Label>
                                <CptCombobox
                                  codes={getAccessibleCPTCodes(appointmentFormData.provider_id).filter(code => !code.is_addon_code)}
                                  value={appointmentFormData.service_type}
                                  valueMode="legacy-label"
                                  testId="appointment-service-type"
                                  placeholder="Select CPT code"
                                  onChange={(value) => {
                                void 0 && console.log('=== CPT SELECTION DEBUG ===');
                                void 0 && console.log('Selected value from dropdown:', value);
                                void 0 && console.log('Total CPT codes in state:', cptCodes?.length);
                                void 0 && console.log('All CPT codes:', cptCodes?.map(c => ({
                                  code: c.code,
                                  display_name: c.display_name,
                                  minutes_per_unit: c.minutes_per_unit,
                                  duration_minutes: c.duration_minutes
                                })));
                                
                                // Find the selected CPT code using the accessible CPT codes for this provider
                                const availableCptCodes = getAccessibleCPTCodes(appointmentFormData.provider_id);
                                
                                void 0 && console.log('Available CPT codes count:', availableCptCodes.length);
                                void 0 && console.log('Looking for match...');
                                
                                const selectedCpt = availableCptCodes.find(code => 
                                  `${code.display_name || code.code} - ${code.description}` === value
                                );
                                
                                if (selectedCpt) {
                                  void 0 && console.log('MATCH FOUND!', {
                                    code: selectedCpt.code,
                                    display_name: selectedCpt.display_name,
                                    description: selectedCpt.description,
                                    duration_minutes: selectedCpt.duration_minutes,
                                    minutes_per_unit: selectedCpt.minutes_per_unit,
                                    allows_multiple_units: selectedCpt.allows_multiple_units
                                  });
                                } else {
                                  void 0 && console.log('NO MATCH FOUND - checking all codes:');
                                  availableCptCodes.forEach(code => {
                                    const constructedValue = `${code.display_name || code.code} - ${code.description}`;
                                    void 0 && console.log(`  "${constructedValue}" === "${value}" ?`, constructedValue === value);
                                  });
                                }
                                
                                // Update service type and recalculate end time if CPT has duration
                                setAppointmentFormData(prev => {
                                  void 0 && console.log('Current form state before update:', {
                                    start_time: prev.start_time,
                                    end_time: prev.end_time,
                                    units: prev.units,
                                    service_type: prev.service_type
                                  });
                                  
                                  const newData = {...prev, service_type: value};
                                  
                                  // Store the CPT code ID for backend processing
                                  if (selectedCpt) {
                                    newData.cpt_code_id = selectedCpt.id;
                                  }
                                  
                                  // Store the selected CPT metadata for later use
                                  newData.selectedCptMeta = selectedCpt ? {
                                    allows_multiple_units: selectedCpt.allows_multiple_units,
                                    minutes_per_unit: selectedCpt.minutes_per_unit || selectedCpt.duration_minutes || 60,
                                    code: selectedCpt.code || selectedCpt.display_name,
                                    is_group_session: selectedCpt.is_group_session || false,
                                    default_group_id: selectedCpt.default_group_id
                                  } : null;
                                  
                                  // Auto-populate group if CPT has a default group
                                  if (selectedCpt && selectedCpt.is_group_session && selectedCpt.default_group_id) {
                                    const defaultGroup = clientGroups?.find(g => g.id === selectedCpt.default_group_id);
                                    if (defaultGroup) {
                                      newData.selected_group_id = defaultGroup.id;
                                      newData.client_ids = defaultGroup.members?.map(m => m.client_id) || defaultGroup.client_ids || [];
                                      void 0 && console.log('Auto-populated default group:', defaultGroup.name, 'with', newData.client_ids.length, 'members');
                                    }
                                  }
                                  
                                  void 0 && console.log('Stored CPT metadata:', newData.selectedCptMeta);
                                  
                                  // If CPT doesn't allow multiple units, force units to 1
                                  if (selectedCpt && !selectedCpt.allows_multiple_units) {
                                    newData.units = 1;
                                  } else if (!newData.units || newData.units < 1) {
                                    // Ensure units is always set to at least 1
                                    newData.units = 1;
                                  }
                                  
                                  if (selectedCpt && prev.start_time) {
                                    const startDate = new Date(prev.start_time);
                                    // Use minutes_per_unit for unit-based billing, otherwise use duration_minutes
                                    const minutesPerUnit = selectedCpt.minutes_per_unit || selectedCpt.duration_minutes || 60;
                                    const units = newData.units || 1;
                                    const durationMinutes = minutesPerUnit * units;
                                    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
                                    
                                    // Convert to local datetime string for datetime-local input (YYYY-MM-DDTHH:MM)
                                    const year = endDate.getFullYear();
                                    const month = String(endDate.getMonth() + 1).padStart(2, '0');
                                    const day = String(endDate.getDate()).padStart(2, '0');
                                    const hours = String(endDate.getHours()).padStart(2, '0');
                                    const minutes = String(endDate.getMinutes()).padStart(2, '0');
                                    newData.end_time = `${year}-${month}-${day}T${hours}:${minutes}`;
                                    
                                    void 0 && console.log('CPT Selected:', selectedCpt?.code || selectedCpt?.display_name, 'Minutes per unit:', minutesPerUnit, 'Units:', units, 'Total duration:', durationMinutes, 'Allows multiple units:', selectedCpt.allows_multiple_units, 'Start:', startDate.toLocaleString(), 'End:', newData.end_time);
                                  } else if (selectedCpt && !prev.start_time) {
                                    // If no start time yet, just log the CPT details for when start time is set
                                    void 0 && console.log('CPT Selected (will calculate duration when start time is set):', selectedCpt?.code || selectedCpt?.display_name, 'Minutes per unit:', selectedCpt.minutes_per_unit || selectedCpt.duration_minutes, 'Allows multiple units:', selectedCpt.allows_multiple_units);
                                  } else {
                                    void 0 && console.log('CPT calculation skipped - selectedCpt:', selectedCpt?.code || selectedCpt?.display_name || 'null', 'start_time:', prev.start_time);
                                  }
                                  
                                  // Check if this CPT / service triggers any intake bundles.
                                  // A bundle is "applicable" when any of:
                                  //   - bundle.trigger_cpt_code_ids includes this CPT ID
                                  //   - bundle.trigger_cpt_codes (legacy) includes this CPT code string
                                  //   - bundle.service_type_ids includes the appointment's service-type id
                                  // The service-type match is the common case the user complained
                                  // wasn't being detected (bundles tied to "Intake Visit" service
                                  // type instead of a specific CPT trigger).
                                  if (selectedCpt && intakeBundles.length > 0) {
                                    const cptId = selectedCpt.id;
                                    const serviceTypeId = newData.service_type_id || prev.service_type_id;
                                    void 0 && console.log('=== INTAKE BUNDLE CHECK ===');
                                    void 0 && console.log('Selected CPT ID:', cptId, 'serviceTypeId:', serviceTypeId);
                                    void 0 && console.log('Total intakeBundles:', intakeBundles.length);
                                    void 0 && console.log('IntakeBundles:', intakeBundles.map(b => ({name: b.name, trigger_cpt_code_ids: b.trigger_cpt_code_ids, service_type_ids: b.service_type_ids})));

                                    const matchingBundles = intakeBundles.filter(bundle =>
                                      (bundle.trigger_cpt_code_ids && bundle.trigger_cpt_code_ids.includes(cptId)) ||
                                      // Backwards compatibility: also check old trigger_cpt_codes field
                                      (bundle.trigger_cpt_codes && bundle.trigger_cpt_codes.includes(selectedCpt.code)) ||
                                      // Service-type-tied bundles (the common case).
                                      (serviceTypeId && bundle.service_type_ids && bundle.service_type_ids.includes(serviceTypeId))
                                    );

                                    void 0 && console.log('Matching bundles:', matchingBundles.length);
                                    if (matchingBundles.length > 0) {
                                      setApplicableIntakeBundles(matchingBundles);
                                    } else {
                                      setApplicableIntakeBundles([]);
                                      newData.intake_bundle_id = '';
                                    }
                                  } else {
                                    setApplicableIntakeBundles([]);
                                    newData.intake_bundle_id = '';
                                  }
                                  
                                  return newData;
                                });
                              }}
                            />
                          </div>
                            </div>
                          )}
                          
                          {/* Service Type for group sessions from CPT (standalone) */}
                          {appointmentFormData.appointment_type === 'client_session' && appointmentFormData.selectedCptMeta?.is_group_session && (
                            <div>
                              <Label htmlFor="service_type" className="text-sm">Service Type (CPT Code) *</Label>
                              <CptCombobox
                                codes={getAccessibleCPTCodes(appointmentFormData.provider_id).filter(code => !code.is_addon_code)}
                                value={appointmentFormData.service_type}
                                valueMode="legacy-label"
                                testId="appointment-service-type-group"
                                placeholder="Select CPT code"
                                onChange={(value) => {
                                // Find the selected CPT code using the accessible CPT codes for this provider
                                const availableCptCodes = getAccessibleCPTCodes(appointmentFormData.provider_id);
                                const selectedCpt = availableCptCodes.find(code => 
                                  `${code.display_name || code.code} - ${code.description}` === value
                                );
                                
                                setAppointmentFormData(prev => {
                                  const newData = {...prev, service_type: value};
                                  if (selectedCpt) {
                                    newData.cpt_code_id = selectedCpt.id;
                                    newData.selectedCptMeta = {
                                      allows_multiple_units: selectedCpt.allows_multiple_units,
                                      minutes_per_unit: selectedCpt.minutes_per_unit || selectedCpt.duration_minutes || 60,
                                      code: selectedCpt.code || selectedCpt.display_name,
                                      is_group_session: selectedCpt.is_group_session || false,
                                      default_group_id: selectedCpt.default_group_id
                                    };
                                  }
                                  return newData;
                                });
                              }}
                            />
                            </div>
                          )}
                          
                          
                          {/* Add-on CPT picker — multi-select chip UI.
                              Refactored from a single-select to multi-select in
                              Feb 2026 per user request: nurses need to stack
                              multiple add-ons per encounter (e.g. 99213 +
                              90833 + 90785).  Picker filters by the chosen
                              primary CPT's ``requires_primary_codes`` and
                              hides itself entirely when nothing is compatible
                              (matches legacy UX — no visual clutter on
                              simple encounters). */}
                          {appointmentFormData.appointment_type === 'client_session' && appointmentFormData.cpt_code_id && (
                            <AddonCptPicker
                              primaryCptId={appointmentFormData.cpt_code_id}
                              selectedIds={
                                Array.isArray(appointmentFormData.addon_cpt_code_ids) && appointmentFormData.addon_cpt_code_ids.length
                                  ? appointmentFormData.addon_cpt_code_ids
                                  : (appointmentFormData.addon_cpt_code_id ? [appointmentFormData.addon_cpt_code_id] : [])
                              }
                              onChange={(ids) => {
                                setAppointmentFormData(prev => ({
                                  ...prev,
                                  addon_cpt_code_ids: ids,
                                  // Keep legacy singular in sync for any back-end
                                  // code that still reads the old field.
                                  addon_cpt_code_id: ids.length === 1 ? ids[0] : null,
                                }));
                              }}
                              cptCodes={cptCodes}
                              testIdPrefix="appointment-addon"
                            />
                          )}
                          
                          {appointmentFormData.appointment_type === 'client_session' && (
                            <>
                              {/* Check if selected CPT is a group session */}
                              {appointmentFormData.selectedCptMeta?.is_group_session ? (
                                <>
                                  {/* Group Session - Show group selector and multi-client */}
                                  <div>
                                    <Label htmlFor="client_group">Select Client Group (Optional)</Label>
                                    <Select 
                                      value={appointmentFormData.selected_group_id || ''} 
                                      onValueChange={(value) => {
                                        // When group selected, add all members to client_ids
                                        const group = clientGroups?.find(g => g.id === value);
                                        if (group) {
                                          // Use group's CPT code if available
                                          const groupCptId = group.cpt_code_id;
                                          const groupCpt = groupCptId ? cptCodes?.find(c => c.id === groupCptId) : null;
                                          
                                          let updates = {
                                            selected_group_id: value,
                                            client_ids: [...new Set([...(appointmentFormData.client_ids || []), ...(group.client_ids || [])])]
                                          };
                                          
                                          // Set the CPT code and metadata if the group has one
                                          if (groupCpt) {
                                            const minutesPerUnit = groupCpt.minutes_per_unit || groupCpt.duration_minutes || 60;
                                            const units = groupCpt.units || 1;
                                            
                                            updates.cpt_code_id = group.cpt_code_id;
                                            updates.service_type = groupCpt.code;
                                            updates.selectedCptMeta = {
                                              allows_multiple_units: groupCpt.allows_multiple_units,
                                              minutes_per_unit: minutesPerUnit,
                                              code: groupCpt.code || groupCpt.display_name,
                                              is_group_session: true
                                            };
                                            
                                            // Calculate duration if start_time is already set
                                            if (appointmentFormData.start_time) {
                                              const durationMinutes = minutesPerUnit * units;
                                              const currentStart = new Date(appointmentFormData.start_time);
                                              const newEnd = new Date(currentStart.getTime() + durationMinutes * 60000);
                                              
                                              // Format as local datetime string (not UTC)
                                              const year = newEnd.getFullYear();
                                              const month = String(newEnd.getMonth() + 1).padStart(2, '0');
                                              const day = String(newEnd.getDate()).padStart(2, '0');
                                              const hours = String(newEnd.getHours()).padStart(2, '0');
                                              const mins = String(newEnd.getMinutes()).padStart(2, '0');
                                              updates.end_time = `${year}-${month}-${day}T${hours}:${mins}`;
                                            }
                                          }
                                          
                                          setAppointmentFormData(prev => ({
                                            ...prev, 
                                            ...updates
                                          }));
                                        }
                                      }}
                                    >
                                      <SelectTrigger data-testid="client-group-optional-trigger">
                                        <SelectValue placeholder="Select a group (optional)" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {(clientGroups || []).map(group => (
                                          <SelectItem key={group.id} value={group.id}>
                                            {group.name} ({group.client_ids?.length || 0} members)
                                          </SelectItem>
                                        ))}
                                        {(!clientGroups || clientGroups.length === 0) && (
                                          <div className="px-2 py-1 text-sm text-gray-500">No groups available</div>
                                        )}
                                      </SelectContent>
                                    </Select>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Select a predefined group to auto-add all members
                                    </p>
                                  </div>
                                  
                                  <div>
                                    <Label>Selected Clients ({(appointmentFormData.client_ids || []).length})</Label>
                                    <div className="border rounded p-2 max-h-40 overflow-y-auto">
                                      {(appointmentFormData.client_ids || []).length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                          {(appointmentFormData.client_ids || []).map(clientId => {
                                            const client = allClientsForLookup?.find(c => c.id === clientId) || clients?.find(c => c.id === clientId);
                                            return client ? (
                                              <Badge 
                                                key={clientId} 
                                                variant="secondary"
                                                className="cursor-pointer hover:bg-red-100"
                                                onClick={() => {
                                                  setAppointmentFormData(prev => ({
                                                    ...prev,
                                                    client_ids: prev.client_ids.filter(id => id !== clientId)
                                                  }));
                                                }}
                                              >
                                                {formatClientName(client)} ✕
                                              </Badge>
                                            ) : null;
                                          })}
                                        </div>
                                      ) : (
                                        <p className="text-sm text-gray-400">No clients selected yet</p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <Label>Add Individual Clients</Label>
                                    <Select 
                                      value="" 
                                      onValueChange={(value) => {
                                        if (value && !(appointmentFormData.client_ids || []).includes(value)) {
                                          setAppointmentFormData(prev => ({
                                            ...prev,
                                            client_ids: [...(prev.client_ids || []), value]
                                          }));
                                        }
                                      }}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Add individual clients" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {getAccessibleClients(appointmentFormData.provider_id)
                                          .filter(c => !(appointmentFormData.client_ids || []).includes(c.id))
                                          .map(client => (
                                            <SelectItem key={client.id} value={client.id}>
                                              {formatClientName(client)}
                                            </SelectItem>
                                          ))}
                                      </SelectContent>
                                    </Select>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Add clients individually to this session
                                    </p>
                                  </div>
                                </>
                              ) : null}
                            </>
                          )}
                          
                          {/* Row 3: Start Time | End Time | Units
                              Jun 2026 (user report) — inline field-level
                              validation so incomplete datetimes / cleared
                              units surface HERE instead of as a backend
                              422 ("input is too short" / "unable to parse
                              string as an integer"). */}
                          {(() => {
                            const startVal = appointmentFormData.start_time;
                            const endVal = appointmentFormData.end_time;
                            const startDateObj = startVal ? new Date(startVal) : null;
                            const endDateObj = endVal ? new Date(endVal) : null;
                            const startErr = !startVal
                              ? 'Start time is required'
                              : (isNaN(startDateObj.getTime()) ? 'Enter a full date and time' : null);
                            const endErr = !endVal
                              ? 'End time is required'
                              : (isNaN(endDateObj.getTime())
                                ? 'Enter a full date and time'
                                : (!startErr && endDateObj <= startDateObj ? 'Must be after the start time' : null));
                            const unitsNum = Number(appointmentFormData.units);
                            const unitsErr = (appointmentFormData.units === '' || appointmentFormData.units === null
                              || appointmentFormData.units === undefined
                              || !Number.isInteger(unitsNum) || unitsNum < 1)
                              ? 'Enter a whole number (1 or more)' : null;
                            const errCls = 'border-red-500 focus-visible:ring-red-500';
                            return (
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                              <Label htmlFor="start_time" className="text-sm">Start</Label>
                              <DateTimeField
                                id="start_time"
                                value={appointmentFormData.start_time}
                                invalid={!!startErr}
                                testId="appointment-start-time"
                                onChange={(newStartTime) => {
                                  setAppointmentFormData(prev => {
                                    const newData = {...prev, start_time: newStartTime};
                                    
                                    // If CPT metadata exists, recalculate end time
                                    if (prev.selectedCptMeta && newStartTime) {
                                      const startDate = new Date(newStartTime);
                                      const minutesPerUnit = prev.selectedCptMeta.minutes_per_unit;
                                      const units = prev.units || 1;
                                      const durationMinutes = minutesPerUnit * units;
                                      const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
                                      
                                      const year = endDate.getFullYear();
                                      const month = String(endDate.getMonth() + 1).padStart(2, '0');
                                      const day = String(endDate.getDate()).padStart(2, '0');
                                      const hours = String(endDate.getHours()).padStart(2, '0');
                                      const minutes = String(endDate.getMinutes()).padStart(2, '0');
                                      newData.end_time = `${year}-${month}-${day}T${hours}:${minutes}`;
                                    } else if (newStartTime && prev.start_time && prev.end_time) {
                                      // No CPT (internal/supervision/etc.) — preserve the
                                      // existing duration by shifting end with start.
                                      const durMs = new Date(prev.end_time) - new Date(prev.start_time);
                                      if (Number.isFinite(durMs) && durMs > 0) {
                                        const endDate = new Date(new Date(newStartTime).getTime() + durMs);
                                        const year = endDate.getFullYear();
                                        const month = String(endDate.getMonth() + 1).padStart(2, '0');
                                        const day = String(endDate.getDate()).padStart(2, '0');
                                        const hours = String(endDate.getHours()).padStart(2, '0');
                                        const minutes = String(endDate.getMinutes()).padStart(2, '0');
                                        newData.end_time = `${year}-${month}-${day}T${hours}:${minutes}`;
                                      }
                                    }
                                    
                                    return newData;
                                  });
                                }}
                              />
                              {startErr && (
                                <p className="text-xs text-red-600 mt-0.5" data-testid="appointment-start-time-error">{startErr}</p>
                              )}
                            </div>
                            <div>
                              <Label htmlFor="end_time" className="text-sm">End</Label>
                              <DateTimeField
                                id="end_time"
                                value={appointmentFormData.end_time}
                                invalid={!!endErr}
                                testId="appointment-end-time"
                                onChange={(v) => setAppointmentFormData(prev => ({...prev, end_time: v}))}
                              />
                              {endErr && (
                                <p className="text-xs text-red-600 mt-0.5" data-testid="appointment-end-time-error">{endErr}</p>
                              )}
                            </div>
                            <div>
                              <Label htmlFor="units" className="text-sm">Units</Label>
                              <Input
                                id="units"
                                type="number"
                                min="1"
                                value={appointmentFormData.units || 1}
                                disabled={appointmentFormData.selectedCptMeta && !appointmentFormData.selectedCptMeta.allows_multiple_units}
                                onChange={(e) => {
                                  const newUnits = parseInt(e.target.value) || 1;
                                  setAppointmentFormData(prev => {
                                    const newData = {...prev, units: newUnits};
                                    
                                    if (prev.service_type && prev.start_time) {
                                      let availableCptCodes = [];
                                      if (isSuperAdmin(user) || user?.role_id === null || !user?.role_id) {
                                        availableCptCodes = cptCodes || [];
                                      } else {
                                        const assignedCptIds = new Set(user?.assigned_cpt_codes || []);
                                        (user?.assigned_cpt_bundles || []).forEach(bundleId => {
                                          const bundle = cptBundles?.find(b => b.id === bundleId);
                                          if (bundle && bundle.cpt_code_ids) {
                                            bundle.cpt_code_ids.forEach(cptId => assignedCptIds.add(cptId));
                                          }
                                        });
                                        availableCptCodes = (cptCodes || []).filter(code => assignedCptIds.has(code.id));
                                      }
                                      
                                      const selectedCpt = availableCptCodes.find(code => 
                                        `${code.display_name || code.code} - ${code.description}` === prev.service_type
                                      );
                                      
                                      if (selectedCpt) {
                                        const startDate = new Date(prev.start_time);
                                        const minutesPerUnit = selectedCpt.minutes_per_unit || selectedCpt.duration_minutes || 60;
                                        const durationMinutes = minutesPerUnit * newUnits;
                                        const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
                                        
                                        const year = endDate.getFullYear();
                                        const month = String(endDate.getMonth() + 1).padStart(2, '0');
                                        const day = String(endDate.getDate()).padStart(2, '0');
                                        const hours = String(endDate.getHours()).padStart(2, '0');
                                        const minutes = String(endDate.getMinutes()).padStart(2, '0');
                                        newData.end_time = `${year}-${month}-${day}T${hours}:${minutes}`;
                                      }
                                    }
                                    
                                    return newData;
                                  });
                                }}
                                placeholder="1"
                                className={`h-9 ${unitsErr ? errCls : ''}`}
                                data-testid="appointment-units"
                              />
                              {unitsErr && (
                                <p className="text-xs text-red-600 mt-0.5" data-testid="appointment-units-error">{unitsErr}</p>
                              )}
                            </div>
                          </div>
                            );
                          })()}
                          
                          {/* CPT Code field has been moved earlier in the form - removed duplicate */}
                          
                          {/* Group Session specific fields */}
                          {appointmentFormData.appointment_type === 'group' && (
                            <>
                              <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label htmlFor="group_select">Select Group *</Label>
                                <Select 
                                  value={appointmentFormData.selected_group_id || ''} 
                                  onValueChange={(value) => {
                                    const group = clientGroups?.find(g => g.id === value);
                                    if (group) {
                                      void 0 && console.log('=== GROUP SELECTED ===');
                                      void 0 && console.log('Group:', group.name);
                                      void 0 && console.log('Group CPT ID:', group.cpt_code_id);
                                      
                                      // Get active member client IDs
                                      const activeClientIds = (group.members || [])
                                        .filter(m => m.status === 'active')
                                        .map(m => m.client_id);
                                      
                                      // Use group's CPT code if available
                                      const groupCptId = group.cpt_code_id || appointmentFormData.cpt_code_id;
                                      const groupCpt = groupCptId ? cptCodes?.find(c => c.id === groupCptId) : null;
                                      
                                      void 0 && console.log('Group CPT found:', groupCpt ? groupCpt.code : 'NOT FOUND');
                                      if (groupCpt) {
                                        void 0 && console.log('CPT minutes_per_unit:', groupCpt.minutes_per_unit);
                                        void 0 && console.log('CPT duration_minutes:', groupCpt.duration_minutes);
                                        void 0 && console.log('CPT units:', groupCpt.units);
                                      }
                                      
                                      // Set service_type to CPT code if available, otherwise group name
                                      const serviceTypeValue = groupCpt 
                                        ? `${groupCpt.code} - ${groupCpt.description || groupCpt.display_name || ''}`
                                        : group.name;
                                      
                                      let updates = {
                                        selected_group_id: value,
                                        client_ids: activeClientIds,
                                        service_type: serviceTypeValue,
                                        cpt_code_id: groupCptId
                                      };
                                      
                                      // Always store CPT metadata if we have a CPT code (so start_time change can use it)
                                      if (groupCpt) {
                                        const minutesPerUnit = groupCpt.minutes_per_unit || groupCpt.duration_minutes || 60;
                                        const units = groupCpt.units || 1;
                                        
                                        void 0 && console.log('Calculated minutesPerUnit:', minutesPerUnit);
                                        void 0 && console.log('Calculated units:', units);
                                        void 0 && console.log('Total duration will be:', minutesPerUnit * units, 'minutes');
                                        
                                        updates.selectedCptMeta = {
                                          allows_multiple_units: groupCpt.allows_multiple_units,
                                          minutes_per_unit: minutesPerUnit,
                                          code: groupCpt.code || groupCpt.display_name,
                                          is_group_session: true
                                        };
                                        
                                        // Calculate duration if start_time is already set
                                        if (appointmentFormData.start_time) {
                                          const durationMinutes = minutesPerUnit * units;
                                          const currentStart = new Date(appointmentFormData.start_time);
                                          const newEnd = new Date(currentStart.getTime() + durationMinutes * 60000);
                                          
                                          // Format as local datetime string (not UTC) for datetime-local input
                                          const year = newEnd.getFullYear();
                                          const month = String(newEnd.getMonth() + 1).padStart(2, '0');
                                          const day = String(newEnd.getDate()).padStart(2, '0');
                                          const hours = String(newEnd.getHours()).padStart(2, '0');
                                          const mins = String(newEnd.getMinutes()).padStart(2, '0');
                                          updates.end_time = `${year}-${month}-${day}T${hours}:${mins}`;
                                          
                                          void 0 && console.log('Start time was set, calculated end_time:', updates.end_time);
                                        } else {
                                          void 0 && console.log('Start time NOT set yet, will calculate on start_time change');
                                        }
                                      }
                                      
                                      void 0 && console.log('Updates being applied:', updates);
                                      
                                      setAppointmentFormData(prev => ({
                                        ...prev, 
                                        ...updates
                                      }));
                                    }
                                  }}
                                  required
                                >
                                  <SelectTrigger data-testid="group-select-trigger">
                                    <SelectValue placeholder="Select a group" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(clientGroups || [])
                                      .filter(g => (g.status === 'active' || g.is_active === true || (g.is_active !== false && !g.status)))
                                      .map(group => {
                                        const activeMemberCount = (group.members || []).filter(m => m.status === 'active').length;
                                        const hasCurriculum = !!group.curriculum_id;
                                        return (
                                          <SelectItem key={group.id} value={group.id}>
                                            {group.name} ({activeMemberCount} members) {hasCurriculum && '📖'}
                                          </SelectItem>
                                        );
                                      })}
                                    {(!clientGroups || clientGroups.filter(g => (g.status === 'active' || g.is_active === true || (g.is_active !== false && !g.status))).length === 0) && (
                                      <div className="px-2 py-1 text-sm text-gray-500">No active groups available</div>
                                    )}
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-gray-500 mt-1">📖 = Has curriculum assigned</p>
                              </div>
                              
                              {/* CPT Code for group sessions */}
                              <div>
                                <Label htmlFor="group_cpt">CPT Code (for billing)</Label>
                                <Select 
                                  value={appointmentFormData.cpt_code_id || 'none'} 
                                  onValueChange={(value) => {
                                    const selectedCptId = value === 'none' ? '' : value;
                                    const selectedCpt = selectedCptId ? cptCodes?.find(c => c.id === selectedCptId) : null;
                                    
                                    let updates = { cpt_code_id: selectedCptId };
                                    
                                    // Calculate duration from CPT code when changed
                                    if (selectedCpt && appointmentFormData.start_time) {
                                      const minutesPerUnit = selectedCpt.minutes_per_unit || selectedCpt.duration_minutes || 60;
                                      const units = selectedCpt.units || 1;
                                      const durationMinutes = minutesPerUnit * units;
                                      
                                      const currentStart = new Date(appointmentFormData.start_time);
                                      const newEnd = new Date(currentStart.getTime() + durationMinutes * 60000);
                                      
                                      // Format as local datetime string (not UTC)
                                      const year = newEnd.getFullYear();
                                      const month = String(newEnd.getMonth() + 1).padStart(2, '0');
                                      const day = String(newEnd.getDate()).padStart(2, '0');
                                      const hours = String(newEnd.getHours()).padStart(2, '0');
                                      const mins = String(newEnd.getMinutes()).padStart(2, '0');
                                      updates.end_time = `${year}-${month}-${day}T${hours}:${mins}`;
                                      
                                      updates.selectedCptMeta = {
                                        allows_multiple_units: selectedCpt.allows_multiple_units,
                                        minutes_per_unit: minutesPerUnit,
                                        code: selectedCpt.code || selectedCpt.display_name,
                                        is_group_session: true
                                      };
                                    }
                                    
                                    setAppointmentFormData(prev => ({...prev, ...updates}));
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select CPT code" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">No CPT Code</SelectItem>
                                    {cptCodes
                                      .filter(cpt => cpt.is_group_session === true)
                                      .map(cpt => (
                                        <SelectItem key={cpt.id} value={cpt.id}>
                                          {cpt.code} - {cpt.display_name || cpt.description} ({cpt.minutes_per_unit} min)
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-gray-500 mt-1">Select a group CPT code for billing purposes</p>
                              </div>
                              </div>
                              
                              {/* Selected Members Preview */}
                              {appointmentFormData.selected_group_id && (
                                <div>
                                  <Label>Group Members ({(appointmentFormData.client_ids || []).length})</Label>
                                  <div className="border rounded p-2 max-h-40 overflow-y-auto bg-gray-50">
                                    {(appointmentFormData.client_ids || []).length > 0 ? (
                                      <div className="flex flex-wrap gap-1">
                                        {(appointmentFormData.client_ids || []).map(clientId => {
                                          const client = allClientsForLookup?.find(c => c.id === clientId) || clients?.find(c => c.id === clientId);
                                          // Get attendance status from the editing appointment
                                          const attendanceStatus = editingAppointment?.client_attendance?.[clientId];
                                          
                                          // Determine badge color based on attendance
                                          let badgeClass = "bg-gray-100 text-gray-700"; // Default/pending
                                          let statusIcon = "";
                                          if (attendanceStatus === 'attended') {
                                            badgeClass = "bg-green-100 text-green-800 border border-green-300";
                                            statusIcon = "✓ ";
                                          } else if (attendanceStatus === 'no_show') {
                                            badgeClass = "bg-red-100 text-red-800 border border-red-300";
                                            statusIcon = "✗ ";
                                          } else if (attendanceStatus === 'cancelled') {
                                            badgeClass = "bg-yellow-100 text-yellow-800 border border-yellow-300";
                                            statusIcon = "⚠ ";
                                          }
                                          
                                          return client ? (
                                            <Badge key={clientId} variant="secondary" className={badgeClass}>
                                              {statusIcon}{formatClientName(client)}
                                            </Badge>
                                          ) : null;
                                        })}
                                      </div>
                                    ) : (
                                      <p className="text-sm text-gray-400">No members in this group</p>
                                    )}
                                  </div>
                                  {/* Attendance Legend - only show when editing and has attendance data */}
                                  {editingAppointment?.client_attendance && Object.keys(editingAppointment.client_attendance).length > 0 && (
                                    <div className="flex gap-3 mt-2 text-xs text-gray-500">
                                      <span className="flex items-center gap-1">
                                        <span className="w-3 h-3 rounded bg-green-200 border border-green-400"></span> Attended
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <span className="w-3 h-3 rounded bg-red-200 border border-red-400"></span> No Show
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <span className="w-3 h-3 rounded bg-yellow-200 border border-yellow-400"></span> Cancelled
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Location Toggle for group sessions */}
                              <div>
                                <Label>Location</Label>
                                <div className="flex gap-2 mt-2">
                                  <Button
                                    type="button"
                                    variant={appointmentFormData.pos_code !== '02' && appointmentFormData.pos_code !== '10' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setAppointmentFormData(prev => ({
                                      ...prev, 
                                      pos_code: '11',
                                      google_meet_link: null
                                    }))}
                                    className="flex-1"
                                  >
                                    <MapPin className="h-4 w-4 mr-2" />
                                    In Person
                                  </Button>
                                  <Button
                                    type="button"
                                    variant={(appointmentFormData.pos_code === '02' || appointmentFormData.pos_code === '10') ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        const response = await axios.post(`${API}/generate-google-meet`, {
                                          provider_id: appointmentFormData.provider_id,
                                          start_time: appointmentFormData.start_time,
                                          end_time: appointmentFormData.end_time,
                                          service_type: appointmentFormData.service_type,
                                          group_id: appointmentFormData.selected_group_id
                                        });
                                        setAppointmentFormData(prev => ({
                                          ...prev, 
                                          pos_code: '02',
                                          google_meet_link: response.data.meet_link
                                        }));
                                        toast.success('Google Meet link generated');
                                      } catch (error) {
                                        console.error('Failed to generate Meet link:', error);
                                        toast.error('Could not generate Google Meet link');
                                      }
                                    }}
                                    className="flex-1"
                                  >
                                    <Video className="h-4 w-4 mr-2" />
                                    Google Meet
                                  </Button>
                                </div>
                                {appointmentFormData.google_meet_link && (
                                  <div className="flex items-center gap-2 mt-1">
                                    <p className="text-xs text-blue-600 truncate flex-1">
                                      {appointmentFormData.google_meet_link}
                                    </p>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-blue-600 hover:bg-blue-50 flex-shrink-0"
                                      data-testid="copy-meet-link-create-btn"
                                      title="Copy video link to send to the client"
                                      onClick={async () => {
                                        try {
                                          await navigator.clipboard.writeText(appointmentFormData.google_meet_link);
                                          toast.success('Video link copied — paste it into an email or text to the client');
                                        } catch {
                                          window.prompt('Copy the video link below:', appointmentFormData.google_meet_link);
                                        }
                                      }}
                                    >
                                      <Copy className="h-3 w-3 mr-1" />
                                      Copy
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                          
                          {appointmentFormData.appointment_type !== 'client_session' && appointmentFormData.appointment_type !== 'group' && (
                            <>
                              <div>
                                <Label htmlFor="service_type">
                                  Description {['supervision', 'internal'].includes(appointmentFormData.appointment_type) ? '(optional)' : '*'}
                                </Label>
                                <DebouncedInput
                                  id="service_type"
                                  value={appointmentFormData.service_type}
                                  onChange={(e) => setAppointmentFormData(prev => ({...prev, service_type: e.target.value}))}
                                  placeholder="e.g., Lunch with team, Annual leave, Staff supervision"
                                  required={!['supervision', 'internal'].includes(appointmentFormData.appointment_type)}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  {appointmentFormData.appointment_type === 'internal' && 'Payroll-eligible internal meeting'}
                                  {appointmentFormData.appointment_type === 'supervision' && 'Payroll-eligible supervision session'}
                                  {appointmentFormData.appointment_type === 'block_time' && 'Non-billable time block'}
                                  {(appointmentFormData.appointment_type === 'vacation' || appointmentFormData.appointment_type === 'sick_day') && 'Full day or partial day off'}
                                </p>
                              </div>
                              
                              {/* Location Toggle for non-client appointments */}
                              <div>
                                <Label>Location</Label>
                                <div className="flex gap-2 mt-2">
                                  <Button
                                    type="button"
                                    variant={appointmentFormData.pos_code !== '02' && appointmentFormData.pos_code !== '10' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setAppointmentFormData(prev => ({
                                      ...prev, 
                                      pos_code: '11',
                                      google_meet_link: null
                                    }))}
                                    className="flex-1"
                                  >
                                    <MapPin className="h-4 w-4 mr-2" />
                                    In Person
                                  </Button>
                                  <Button
                                    type="button"
                                    variant={(appointmentFormData.pos_code === '02' || appointmentFormData.pos_code === '10') ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={async () => {
                                      // Generate Google Meet link
                                      try {
                                        const response = await axios.post(`${API}/generate-google-meet`, {
                                          client_id: appointmentFormData.client_id,
                                          provider_id: appointmentFormData.provider_id,
                                          start_time: appointmentFormData.start_time,
                                          end_time: appointmentFormData.end_time,
                                          service_type: appointmentFormData.service_type,
                                          group_id: appointmentFormData.selected_group_id
                                        });
                                        setAppointmentFormData(prev => ({
                                          ...prev, 
                                          pos_code: '02',
                                          google_meet_link: response.data.meet_link
                                        }));
                                        toast.success('Google Meet link generated');
                                      } catch (error) {
                                        console.error('Error generating Meet link:', error);
                                        toast.error('Failed to generate Google Meet link');
                                      }
                                    }}
                                    className="flex-1"
                                  >
                                    <Video className="h-4 w-4 mr-2" />
                                    Google Meet
                                  </Button>
                                </div>
                                {appointmentFormData.google_meet_link && (
                                  <div className="mt-2 space-y-2">
                                    <p className="text-xs text-green-600 flex items-center">
                                      <Video className="h-3 w-3 mr-1" />
                                      Google Meet link created
                                    </p>
                                    <div className="flex gap-2">
                                    <Button 
                                      type="button"
                                      onClick={() => {
                                        openMeetPopup(appointmentFormData.google_meet_link);
                                      }}
                                      className="bg-green-600 hover:bg-green-700 text-white text-sm"
                                      size="sm"
                                    >
                                      <Video className="h-4 w-4 mr-2" />
                                      Start Session
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="border-green-300 text-green-700 hover:bg-green-100"
                                      data-testid="copy-meet-link-nonclient-btn"
                                      onClick={async () => {
                                        try {
                                          await navigator.clipboard.writeText(appointmentFormData.google_meet_link);
                                          toast.success('Video link copied — paste it into an email or text');
                                        } catch {
                                          window.prompt('Copy the video link below:', appointmentFormData.google_meet_link);
                                        }
                                      }}
                                    >
                                      <Copy className="h-4 w-4 mr-2" />
                                      Copy Link
                                    </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                          
                          {/* Row 4: Location | POS | Modality - 3-column grid */}
                          {(appointmentFormData.appointment_type === 'client_session' || appointmentFormData.appointment_type === 'group') && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              {/* Location */}
                              <div>
                                <Label htmlFor="location" className="text-sm">Location *</Label>
                                <Select
                                  key={`location-${appointmentFormData.provider_id}`}
                                  value={appointmentFormData.location || undefined}
                                  onValueChange={(value) => setAppointmentFormData(prev => {
                                    const updates = { location: value };
                                    // Provider's Home narrows POS to telehealth codes — clear an
                                    // incompatible POS so a hidden stale code can't be saved.
                                    if (value === "Provider's Home" && prev.pos_code && !['02', '10'].includes(prev.pos_code)) {
                                      updates.pos_code = '';
                                    }
                                    return { ...prev, ...updates };
                                  })}
                                  required
                                >
                                  <SelectTrigger className="h-9" data-testid="appt-location-select">
                                    <SelectValue placeholder="Select location" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(() => {
                                      const selectedProvider = providers?.find(p => p.id === appointmentFormData.provider_id) || users?.find(u => u.id === appointmentFormData.provider_id);
                                      const providerLocations = selectedProvider?.assigned_locations || [];
                                      // Jun 2026 — bidirectional telehealth linking: Provider's Home
                                      // is always offered so the user can start from Location too;
                                      // selecting it filters POS/modality to telehealth options.
                                      return (
                                        <>
                                          {providerLocations.map(location => (
                                            <SelectItem key={location} value={location}>
                                              {location}
                                            </SelectItem>
                                          ))}
                                          <SelectItem value="Provider's Home">
                                            Provider's Home (telehealth)
                                          </SelectItem>
                                        </>
                                      );
                                    })()}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              {/* POS Code */}
                              <div>
                                <Label htmlFor="pos_code" className="text-sm">POS *</Label>
                                <Select
                                  key={`pos-${appointmentFormData.provider_id}`}
                                  value={appointmentFormData.pos_code || undefined}
                                  onValueChange={(value) => {
                                    const updates = { pos_code: value };
                                    if (value === '02' || value === '10') {
                                      // Additive: only default the modality when none is chosen yet.
                                      if (!['audio_visual', 'audio_only'].includes(appointmentFormData.telehealth_modality)) {
                                        const selectedProvider = providers?.find(p => p.id === appointmentFormData.provider_id) || users?.find(u => u.id === appointmentFormData.provider_id);
                                        updates.telehealth_modality = selectedProvider?.default_telehealth_modality || 'audio_visual';
                                      }
                                    } else {
                                      updates.telehealth_modality = '';
                                    }
                                    setAppointmentFormData(prev => ({...prev, ...updates}));
                                  }}
                                  required
                                >
                                  <SelectTrigger className="h-9" data-testid="appt-pos-select">
                                    <SelectValue placeholder="Select POS" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(() => {
                                      const selectedProvider = providers?.find(p => p.id === appointmentFormData.provider_id) || users?.find(u => u.id === appointmentFormData.provider_id);
                                      const providerPosCodesWithDesc = selectedProvider?.assigned_pos_codes_with_descriptions || [];
                                      const providerPosCodes = selectedProvider?.assigned_pos_codes || [];
                                      const allPosCodes = getEffectivePosCodes(organizations, posCodes);
                                      // Jun 2026 — bidirectional telehealth linking: a telehealth
                                      // modality or Provider's Home location filters POS to the
                                      // telehealth codes (02/10). Additive: the current selection is
                                      // never cleared, only the option list narrows.
                                      const TELE_POS = ['02', '10'];
                                      const teleLinked = ['audio_visual', 'audio_only'].includes(appointmentFormData.telehealth_modality)
                                        || appointmentFormData.location === "Provider's Home";
                                      
                                      if (teleLinked) {
                                        const teleFromProvider = providerPosCodesWithDesc.length > 0
                                          ? providerPosCodesWithDesc.filter(p => TELE_POS.includes(p.code))
                                          : providerPosCodes.filter(c => TELE_POS.includes(c)).map(c => ({
                                              code: c,
                                              description: allPosCodes.find(p => p.code === c)?.description || STANDARD_POS_DESCRIPTIONS[c] || `Place of Service ${c}`,
                                            }));
                                        // Additive: telehealth codes stay offered even when the
                                        // provider has none assigned, so the user isn't dead-ended.
                                        const telePos = teleFromProvider.length > 0 ? teleFromProvider : TELE_POS.map(c => ({
                                          code: c,
                                          description: allPosCodes.find(p => p.code === c)?.description || STANDARD_POS_DESCRIPTIONS[c] || `Place of Service ${c}`,
                                        }));
                                        return telePos.map(pos => (
                                          <SelectItem key={pos.code} value={pos.code}>
                                            {pos.code} - {pos.description}
                                          </SelectItem>
                                        ));
                                      }
                                      
                                      if (providerPosCodes.length === 0) {
                                        return <SelectItem value="none" disabled>Provider needs POS codes assigned</SelectItem>;
                                      }
                                      
                                      if (providerPosCodesWithDesc.length > 0) {
                                        return providerPosCodesWithDesc.map(pos => (
                                          <SelectItem key={pos.code} value={pos.code}>
                                            {pos.code} - {pos.description}
                                          </SelectItem>
                                        ));
                                      }
                                      
                                      return providerPosCodes.map(posCode => (
                                        <SelectItem key={posCode} value={posCode}>
                                          {posCode} - {allPosCodes.find(p => p.code === posCode)?.description || STANDARD_POS_DESCRIPTIONS[posCode] || `Place of Service ${posCode}`}
                                        </SelectItem>
                                      ));
                                    })()}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              {/* Modality — always selectable (Jun 2026 bidirectional
                                  telehealth linking): picking a telehealth modality adds
                                  Provider's Home to locations and filters POS to 02/10.
                                  In-Person is ALWAYS offered as the escape hatch back to
                                  in-person — picking it clears telehealth POS/location. */}
                              <div>
                                <Label htmlFor="telehealth_modality" className="text-sm">Modality</Label>
                                {(() => {
                                  const posIsTele = appointmentFormData.pos_code === '02' || appointmentFormData.pos_code === '10';
                                  const locIsTele = appointmentFormData.location === "Provider's Home";
                                  const modalityValue = ['audio_visual', 'audio_only'].includes(appointmentFormData.telehealth_modality)
                                    ? appointmentFormData.telehealth_modality
                                    : ((posIsTele || locIsTele) ? '' : 'in_person');
                                  return (
                                    <Select
                                      value={modalityValue}
                                      onValueChange={(value) => setAppointmentFormData(prev => {
                                        if (value === 'in_person') {
                                          // Explicit return to in-person: clear telehealth signals.
                                          const updates = { telehealth_modality: '' };
                                          if (['02', '10'].includes(prev.pos_code)) updates.pos_code = '';
                                          if (prev.location === "Provider's Home") updates.location = '';
                                          return { ...prev, ...updates, google_meet_link: null };
                                        }
                                        const updates = { telehealth_modality: value };
                                        // Telehealth narrows POS to 02/10 — clear an incompatible
                                        // POS so a hidden stale code can't be saved.
                                        if (prev.pos_code && !['02', '10'].includes(prev.pos_code)) {
                                          updates.pos_code = '';
                                        }
                                        return { ...prev, ...updates };
                                      })}
                                    >
                                      <SelectTrigger className="h-9" data-testid="appt-modality-select">
                                        <SelectValue placeholder="Select modality" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="in_person">In-Person</SelectItem>
                                        <SelectItem value="audio_visual">Telehealth — Audio-Visual (95)</SelectItem>
                                        <SelectItem value="audio_only">Telehealth — Audio Only (93)</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  );
                                })()}
                              </div>
                            </div>
                          )}

                          {/* Jun 2026 — recent assessments + optional
                              quick-send, visible whenever a client is
                              selected (create OR edit). */}
                          <AppointmentAssessmentsSection clientId={appointmentFormData.client_id} />

                          {/* Intake Bundle Selection — Feb 2026 (v2): shown
                              only when the chosen CPT/service-type matches at
                              least one intake bundle.  Matching considers
                              `trigger_cpt_code_ids`, the legacy
                              `trigger_cpt_codes` string list, AND
                              `service_type_ids` (so bundles tied to "Intake
                              Visit" type are surfaced too).  v1 of this
                              change rendered on ANY bundle existing, which
                              put the picker on every appointment — that was
                              the user complaint we're fixing here. */}
                          {(() => {
                            // Jun 2026 — intake bundles can now be sent at ANY
                            // time from an existing appointment, even if none
                            // was attached (or "don't use" was picked) at
                            // scheduling time.  In edit mode we always show the
                            // section with ALL bundles (suggested matches
                            // first); creation flow keeps the original
                            // "only when CPT/service matches" behavior.
                            const isEditingExisting = !!(editingAppointment && editingAppointment.id);
                            if (!appointmentFormData.client_id) return null;
                            if (!isEditingExisting && applicableIntakeBundles.length === 0) return null;
                            if (isEditingExisting && applicableIntakeBundles.length === 0 && intakeBundles.length === 0) return null;
                            const bundleOptions = isEditingExisting
                              ? [
                                  ...applicableIntakeBundles,
                                  ...intakeBundles.filter(b => !applicableIntakeBundles.some(a => a.id === b.id)),
                                ]
                              : applicableIntakeBundles;
                            const sentByUser = appointmentFormData.intake_bundle_sent_by
                              ? (users || []).find(u => u.id === appointmentFormData.intake_bundle_sent_by)
                              : null;
                            return (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden" data-testid="intake-bundle-section">
                              <button
                                type="button"
                                onClick={() => setIntakeSectionExpanded(p => !p)}
                                className="w-full p-3 flex items-center justify-between hover:bg-blue-100 transition-colors"
                                data-testid="intake-bundle-toggle"
                              >
                                <span className="font-semibold text-blue-900 text-sm flex items-center gap-2">
                                  <Folder className="h-4 w-4 text-blue-600" /> Intake Bundle (Optional)
                                  {appointmentFormData.intake_bundle_sent_at ? (
                                    <span className="text-[11px] font-medium bg-green-100 text-green-700 px-2 py-0.5 rounded-full">sent</span>
                                  ) : (isEditingExisting && (
                                    <span className="text-[11px] font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">not sent</span>
                                  ))}
                                </span>
                                <ChevronDown className={`h-4 w-4 text-blue-600 transition-transform ${intakeSectionExpanded ? 'rotate-180' : ''}`} />
                              </button>
                              {intakeSectionExpanded && (
                              <div className="px-4 pb-4 border-t border-blue-200 pt-2">
                              <p className="text-xs text-blue-700 mb-3">
                                {isEditingExisting
                                  ? (appointmentFormData.intake_bundle_sent_at
                                      ? 'An intake bundle has been sent for this appointment.'
                                      : 'No intake bundle has been sent for this appointment yet. Select a bundle below to prepare and send it now — you can do this at any time.')
                                  : 'This appointment can trigger intake documents. Select a bundle to auto-create forms when scheduled.'}
                              </p>
                              <Select
                                value={appointmentFormData.intake_bundle_id || 'none'}
                                onValueChange={(value) => {
                                  setAppointmentFormData(prev => ({
                                    ...prev,
                                    intake_bundle_id: value === 'none' ? '' : value
                                  }));
                                }}
                              >
                                <SelectTrigger data-testid="intake-bundle-select">
                                  <SelectValue placeholder="No intake bundle" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Don't use intake bundle</SelectItem>
                                  {bundleOptions
                                    .filter(bundle => {
                                      // Age filter that respects bundle.age_min / age_max.
                                      if (!bundle.age_min && !bundle.age_max) return true;
                                      const client = allClientsForLookup.find(c => c.id === appointmentFormData.client_id);
                                      if (!client || !client.date_of_birth) return true;
                                      const age = Math.floor((new Date() - new Date(client.date_of_birth)) / 31557600000);
                                      const meetsMin = !bundle.age_min || age >= bundle.age_min;
                                      const meetsMax = !bundle.age_max || age <= bundle.age_max;
                                      return meetsMin && meetsMax;
                                    })
                                    .slice()
                                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                    .map(bundle => (
                                      <SelectItem key={bundle.id} value={bundle.id}>
                                        {bundle.name} ({bundle.documents?.length || 0} documents)
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              {appointmentFormData.intake_bundle_id && (() => {
                                const selectedBundle = applicableIntakeBundles.find(b => b.id === appointmentFormData.intake_bundle_id)
                                  || intakeBundles.find(b => b.id === appointmentFormData.intake_bundle_id);
                                return selectedBundle && (
                                  <div className="mt-3 p-3 bg-white rounded border text-xs">
                                    <p className="font-medium mb-2">📋 Documents that will be created:</p>
                                    <ul className="space-y-1 text-gray-700">
                                      {selectedBundle.documents.map((doc, idx) => (
                                        <li key={idx} className="flex items-start gap-2">
                                          <CheckCircle className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" />
                                          <span>{doc.template_name}</span>
                                        </li>
                                      ))}
                                    </ul>

                                    {/* Mar 2026 — "Send Intake Bundle Now"
                                        button.  User report: "I need a way
                                        to send an intake bundle after I
                                        have scheduled the appointment".
                                        Only renders when we're editing an
                                        existing appointment (creation flow
                                        sends the bundle automatically at
                                        save time per the onSubmit handler).
                                        Disabled while in-flight; re-send
                                        requires confirm. */}
                                    {editingAppointment && editingAppointment.id && (
                                      <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-2">
                                        {appointmentFormData.intake_bundle_sent_at ? (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-medium" data-testid="intake-bundle-sent-badge">
                                            <CheckCircle className="h-3 w-3" />
                                            Sent {new Date(appointmentFormData.intake_bundle_sent_at).toLocaleDateString()} {new Date(appointmentFormData.intake_bundle_sent_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                            {sentByUser ? ` by ${sentByUser.first_name || ''} ${sentByUser.last_name || ''}`.trimEnd() : ''}
                                          </span>
                                        ) : (
                                          <span className="text-xs text-gray-500" data-testid="intake-bundle-not-sent-label">Not sent yet</span>
                                        )}
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant={appointmentFormData.intake_bundle_sent_at ? 'outline' : 'default'}
                                          disabled={!!appointmentFormData._sendingIntakeBundle}
                                          data-testid="send-intake-bundle-btn"
                                          className={appointmentFormData.intake_bundle_sent_at
                                            ? 'border-blue-600 text-blue-600 hover:bg-blue-50'
                                            : 'bg-blue-600 hover:bg-blue-700 text-white'}
                                          onClick={async () => {
                                            if (appointmentFormData.intake_bundle_sent_at) {
                                              const ok = window.confirm(
                                                "Intake bundle already sent on " +
                                                new Date(appointmentFormData.intake_bundle_sent_at).toLocaleString() +
                                                ". Re-send the same packet to the client?"
                                              );
                                              if (!ok) return;
                                            }
                                            // Mar 2026 — Prefill-Before-Send workflow.
                                            // Instead of immediately emailing the client,
                                            // we call the send-intake-bundle endpoint in
                                            // ``prepare_only`` mode (creates notes in
                                            // draft state, no client email), then open
                                            // the IntakeBundlePrefillDialog so the
                                            // provider can fill in any provider-side
                                            // fields BEFORE the client receives the
                                            // packet.  Prevents the "invalidate signature
                                            // to add a name on a consent form" cycle.
                                            setAppointmentFormData(prev => ({ ...prev, _sendingIntakeBundle: true }));
                                            try {
                                              const resp = await axios.post(
                                                `${API}/appointments/${editingAppointment.id}/send-intake-bundle`,
                                                {
                                                  bundle_id: appointmentFormData.intake_bundle_id,
                                                  prepare_only: true,
                                                }
                                              );
                                              setAppointmentFormData(prev => ({
                                                ...prev,
                                                _sendingIntakeBundle: false,
                                                _prefillOpen: true,
                                                _prefillBundleId: resp.data?.bundle_id || appointmentFormData.intake_bundle_id,
                                                _prefillBundleName: resp.data?.bundle_name,
                                                _prefillClientId: resp.data?.client_id,
                                              }));
                                            } catch (err) {
                                              setAppointmentFormData(prev => ({ ...prev, _sendingIntakeBundle: false }));
                                              const detail = err?.response?.data?.detail;
                                              toast.error(typeof detail === 'string' ? detail : 'Failed to prepare intake bundle');
                                            }
                                          }}
                                        >
                                          {appointmentFormData._sendingIntakeBundle
                                            ? 'Preparing…'
                                            : appointmentFormData.intake_bundle_sent_at
                                              ? 'Prefill & Re-send'
                                        : 'Prepare & Send Intake Bundle'}
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              </div>
                              )}
                            </div>
                            );
                          })()}

                        {/* Reminder Status - Collapsible section for scheduled appointments being edited */}
                        {editingAppointment && editingAppointment.status === 'scheduled' && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg mb-4 overflow-hidden">
                            <button
                              type="button"
                              onClick={() => setReminderSectionExpanded(!reminderSectionExpanded)}
                              className="w-full p-3 flex items-center justify-between hover:bg-blue-100 transition-colors"
                            >
                              <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                                <span>📧</span> Reminder Status
                                {editingAppointment.reminders_sent?.length > 0 && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                    {editingAppointment.reminders_sent.length} sent
                                  </span>
                                )}
                              </h3>
                              <ChevronDown className={`h-5 w-5 text-blue-600 transition-transform ${reminderSectionExpanded ? 'rotate-180' : ''}`} />
                            </button>
                            {reminderSectionExpanded && (
                              <div className="px-4 pb-4 space-y-2 text-sm border-t border-blue-200">
                                <div className="pt-3">
                                  {/* Feb 2026 — flag a "data-mismatch" warning
                                      when reminders_sent claims one fired but
                                      the appointment is still more than that
                                      window in the future (e.g., '2_hours'
                                      marked sent for an appointment >2h
                                      away).  This means the array was
                                      stamped by a different path (legacy
                                      backfill, recurring-clone inherit) and
                                      no email actually went out. */}
                                  {(() => {
                                    if (!editingAppointment.reminders_sent?.length) return null;
                                    const st = new Date(editingAppointment.start_time);
                                    const now = new Date();
                                    const hoursUntil = (st - now) / 3600000;
                                    const suspicious = [];
                                    if (editingAppointment.reminders_sent.includes('48_hours') && hoursUntil > 48) suspicious.push('48-hour');
                                    if (editingAppointment.reminders_sent.includes('2_hours') && hoursUntil > 2.5) suspicious.push('2-hour');
                                    if (editingAppointment.reminders_sent.includes('5_minutes') && hoursUntil > 0.1) suspicious.push('5-minute');
                                    if (suspicious.length === 0) return null;
                                    return (
                                      <div
                                        className="mb-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                                        data-testid="reminder-status-mismatch-warning"
                                      >
                                        ⚠️ <span className="font-medium">Data check:</span> {suspicious.join(', ')} reminder{suspicious.length > 1 ? 's' : ''} marked as sent but the appointment is still {hoursUntil.toFixed(1)} hours away.
                                        This usually means the row was inherited from a recurring template — no email actually went out yet.
                                      </div>
                                    );
                                  })()}

                                  {/* 48-hour reminder */}
                                  <div className="flex items-center justify-between bg-white rounded px-3 py-2 border mb-2">
                                    <span className="text-gray-700">48-hour reminder</span>
                                    {editingAppointment.reminders_sent?.includes('48_hours') ? (
                                      <span className="flex items-center gap-1 text-green-600 font-medium">
                                        <span>✅</span> Sent
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-gray-400">
                                        <span>⏳</span> {new Date(editingAppointment.start_time) > new Date(Date.now() + 48 * 60 * 60 * 1000) ? 'Scheduled' : 'Window passed'}
                                      </span>
                                    )}
                                  </div>
                                  
                                  {/* 2-hour reminder */}
                                  <div className="flex items-center justify-between bg-white rounded px-3 py-2 border">
                                    <span className="text-gray-700">2-hour reminder</span>
                                    {editingAppointment.reminders_sent?.includes('2_hours') ? (
                                      <span className="flex items-center gap-1 text-green-600 font-medium">
                                        <span>✅</span> Sent
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-gray-400">
                                        <span>⏳</span> {new Date(editingAppointment.start_time) > new Date(Date.now() + 2 * 60 * 60 * 1000) ? 'Scheduled' : 'Window passed'}
                                      </span>
                                    )}
                                  </div>
                                  
                                  {/* 5-minute reminder */}
                                  <div className="flex items-center justify-between bg-white rounded px-3 py-2 border">
                                    <span className="text-gray-700">5-minute reminder</span>
                                    {editingAppointment.reminders_sent?.includes('5_minutes') ? (
                                      <span className="flex items-center gap-1 text-green-600 font-medium">
                                        <span>✅</span> Sent
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-gray-400">
                                        <span>⏳</span> {new Date(editingAppointment.start_time) > new Date(Date.now() + 5 * 60 * 1000) ? 'Scheduled' : 'Window passed'}
                                      </span>
                                    )}
                                  </div>
                                  
                                  {/* Last reminder sent timestamp */}
                                  {editingAppointment.last_reminder_sent_at && (
                                    <div className="text-xs text-gray-500 mt-3 pt-2 border-t">
                                      Last reminder sent: {new Date(editingAppointment.last_reminder_sent_at).toLocaleDateString()} at {new Date(editingAppointment.last_reminder_sent_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                          
                          {/* Recurring Appointment Options */}
                          {/* Recurring Appointments - Only show when creating NEW appointments */}
                          {!editingAppointment && (
                          <div className="border-t pt-4 mt-4">
                            <div className="flex items-center space-x-2 mb-3">
                              <input
                                type="checkbox"
                                id="is_recurring"
                                checked={appointmentFormData.is_recurring}
                                onChange={(e) => setAppointmentFormData(prev => ({
                                  ...prev,
                                  is_recurring: e.target.checked,
                                  recurrence_pattern: e.target.checked ? 'weekly' : '',
                                  recurrence_count: e.target.checked ? '12' : '',
                                  recurring_days: []
                                }))}
                                className="h-4 w-4"
                              />
                              <Label htmlFor="is_recurring" className="font-semibold">Make this a recurring appointment</Label>
                            </div>
                            
                            {appointmentFormData.is_recurring && (
                              <div className="space-y-3 ml-6">
                                <div>
                                  <Label htmlFor="recurrence_pattern">Repeat Pattern *</Label>
                                  <Select 
                                    value={appointmentFormData.recurrence_pattern} 
                                    onValueChange={(value) => setAppointmentFormData(prev => ({...prev, recurrence_pattern: value}))}
                                    required
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select pattern" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="daily">Daily</SelectItem>
                                      <SelectItem value="weekly">Weekly</SelectItem>
                                      <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                                      <SelectItem value="monthly">Monthly</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {appointmentFormData.recurrence_pattern === 'monthly' && (() => {
                                    const d = new Date(appointmentFormData.start_time);
                                    if (isNaN(d.getTime())) return null;
                                    const dayN = d.getDate();
                                    const wd = d.toLocaleDateString('en-US', { weekday: 'long' });
                                    const ord = Math.floor((dayN - 1) / 7) + 1;
                                    const dim = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
                                    const isLast = dayN + 7 > dim && ord >= 4;
                                    const ordLabel = isLast ? 'Last' : ['1st', '2nd', '3rd', '4th', '5th'][ord - 1];
                                    const mode = appointmentFormData.monthly_mode || 'day_of_month';
                                    return (
                                      <div className="mt-2">
                                        <Select value={mode} onValueChange={(v) => setAppointmentFormData(prev => ({ ...prev, monthly_mode: v }))}>
                                          <SelectTrigger data-testid="monthly-mode-select">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="day_of_month">On day {dayN} of each month</SelectItem>
                                            <SelectItem value="nth_weekday">On the {ordLabel.toLowerCase()} {wd} of each month</SelectItem>
                                          </SelectContent>
                                        </Select>
                                        {mode === 'day_of_month' && dayN > 28 && (
                                          <p className="text-xs text-amber-600 mt-1" data-testid="monthly-clamp-hint">
                                            Day {dayN}: months without that day will use their last day instead (e.g. Feb → Feb 28/29). Choose "{ordLabel.toLowerCase()} {wd}" to avoid this.
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                                
                                {/* Day of week selection for weekly/biweekly */}
                                {(appointmentFormData.recurrence_pattern === 'weekly' || appointmentFormData.recurrence_pattern === 'biweekly') && (
                                  <div>
                                    <Label>Repeat On (select days)</Label>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => {
                                        const isSelected = (appointmentFormData.recurring_days || []).includes(index);
                                        return (
                                          <button
                                            key={day}
                                            type="button"
                                            onClick={() => {
                                              setAppointmentFormData(prev => {
                                                const days = [...(prev.recurring_days || [])];
                                                if (isSelected) {
                                                  return { ...prev, recurring_days: days.filter(d => d !== index) };
                                                } else {
                                                  return { ...prev, recurring_days: [...days, index].sort() };
                                                }
                                              });
                                            }}
                                            className={`px-3 py-1 rounded text-sm border ${
                                              isSelected 
                                                ? 'bg-teal-600 text-white border-teal-600' 
                                                : 'bg-white text-gray-700 border-gray-300 hover:border-teal-400'
                                            }`}
                                          >
                                            {day.slice(0, 3)}
                                          </button>
                                        );
                                      })}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {(!appointmentFormData.recurring_days || appointmentFormData.recurring_days.length === 0)
                                        ? 'Select at least one day, or leave blank to use the start date\'s day' 
                                        : `Will repeat on ${appointmentFormData.recurring_days.map(d => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d]).join(', ')}`
                                      }
                                    </p>
                                  </div>
                                )}
                                
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label htmlFor="recurrence_count">Number of Occurrences</Label>
                                    {(() => {
                                      const recCap = ['block_time', 'supervision'].includes(appointmentFormData.appointment_type) ? 365 : 16;
                                      return (
                                    <Input
                                      id="recurrence_count"
                                      type="number"
                                      min="1"
                                      max={recCap}
                                      value={appointmentFormData.recurrence_count}
                                      onChange={(e) => {
                                        const value = e.target.value ? Math.min(recCap, parseInt(e.target.value) || 1) : '';
                                        setAppointmentFormData(prev => ({...prev, recurrence_count: value.toString(), recurrence_end_date: ''}));
                                      }}
                                      placeholder={`Max ${recCap}`}
                                    />
                                      );
                                    })()}
                                  </div>
                                  <div>
                                    <Label htmlFor="recurrence_end_date">Or End Date</Label>
                                    <DateField
                                      id="recurrence_end_date"
                                      value={appointmentFormData.recurrence_end_date}
                                      onChange={(v) => setAppointmentFormData(prev => ({...prev, recurrence_end_date: v, recurrence_count: ''}))}
                                      testId="recurrence-end-date"
                                    />
                                  </div>
                                </div>
                                
                                {!appointmentFormData.recurrence_count && !appointmentFormData.recurrence_end_date && (
                                  <p className="text-xs text-red-500">Please specify either number of occurrences or an end date</p>
                                )}
                                
                                <p className="text-xs text-gray-500">
                                  {appointmentFormData.recurrence_count && `Will create ${appointmentFormData.recurrence_count} appointments`}
                                  {parseInt(appointmentFormData.recurrence_count) > 12 && (
                                    <span className="text-amber-600"> • High session count - consider authorization limits</span>
                                  )}
                                  {appointmentFormData.recurrence_end_date && `Will repeat until ${new Date(appointmentFormData.recurrence_end_date).toLocaleDateString()}`}
                                </p>
                              </div>
                            )}
                          </div>
                          )}
                          
                          <div className="flex flex-wrap justify-between gap-2">
                            <div className="flex flex-wrap gap-2">
                              {editingAppointment && (() => {
                                const linkedNote = notes.find(n => n.appointment_id === editingAppointment.id);
                                // Also show Unlink when the appt has a provenance note id
                                // that isn't in local state (older appts). Clicking Unlink
                                // in that case will try to clear both sides.
                                const fallbackNoteId = !linkedNote && editingAppointment.created_from_note
                                  ? editingAppointment.created_from_note
                                  : null;
                                const showUnlink = !!linkedNote || !!fallbackNoteId;
                                return (
                                  <>
                                    {showUnlink && (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                        data-testid="unlink-note-btn"
                                        onClick={async (e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          const targetNoteId = linkedNote?.id || fallbackNoteId;
                                          const statusText = linkedNote?.status || 'draft';
                                          if (!window.confirm(
                                            `Unlink the ${statusText} note from this appointment?\n\n` +
                                            `The note will be preserved (its Date of Service stays intact). ` +
                                            `You'll then be able to delete this appointment.`
                                          )) return;
                                          try {
                                            // Preserve date_of_service on unlink.
                                            try {
                                              await axios.put(`${API}/notes/${targetNoteId}`, {
                                                appointment_id: null
                                              });
                                            } catch (noteErr) {
                                              // If note no longer exists we still want to
                                              // clear the stale provenance on the appt.
                                              if (noteErr?.response?.status !== 404) throw noteErr;
                                            }
                                            toast.success('Note unlinked from appointment');
                                            if (editingAppointment.created_from_note) {
                                              try {
                                                await axios.put(`${API}/appointments/${editingAppointment.id}`, {
                                                  created_from_note: null
                                                });
                                              } catch (e2) {
                                                console.warn('[Unlink] Could not clear created_from_note:', e2);
                                              }
                                              setEditingAppointment(prev => prev ? { ...prev, created_from_note: null } : prev);
                                            }
                                            refreshNotes(); refreshAppointments(true);
                                            // ALSO refresh the client-chart notes list
                                            // (separate from global `notes`). Without this,
                                            // the Documents tab on the client chart won't
                                            // show the newly-unlinked note until the user
                                            // reopens the chart or switches tabs.
                                            try {
                                              const cid = linkedNote?.client_id || editingAppointment?.client_id;
                                              if (cid) {
                                                const resp = await axios.get(
                                                  `${API}/notes?client_id=${cid}&start_date=2020-01-01&end_date=2030-12-31&summary=true`
                                                );
                                                setClientNotes(resp.data || []);
                                              }
                                            } catch (e3) {
                                              console.warn('[Unlink] clientNotes refresh failed:', e3);
                                            }
                                          } catch (err) {
                                            toast.error(err.response?.data?.detail || 'Failed to unlink note');
                                          }
                                        }}
                                      >
                                        Unlink Note
                                      </Button>
                                    )}
                                    <Button
                                      type="button"
                                      variant="destructive"
                                      size="sm"
                                      data-testid="delete-appointment-btn"
                                      onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();

                                  // Jun 2026 — NON-DESTRUCTIVE probe first (?probe=true):
                                  // reliably detects a linked note even when it isn't in
                                  // local state (e.g. billed/paid notes filtered views
                                  // don't load), so the in-app 3-action dialog is the
                                  // ONLY confirmation for the linked-note case.
                                  try {
                                    const probe = await axios.delete(
                                      `${API}/appointments/${editingAppointment.id}?probe=true`
                                    );
                                    if (probe.data && probe.data.has_linked_note) {
                                      // Open the in-app 3-action dialog; the
                                      // actual delete/unlink happens there.
                                      setLinkedNoteDeleteInfo({
                                        noteStatus: probe.data.note_status || 'draft',
                                        noteId: probe.data.note_id,
                                        appointment: editingAppointment,
                                      });
                                      return;
                                    }
                                    // No linked note — plain confirm, then real delete.
                                    if (!window.confirm('Are you sure you want to delete this appointment?')) {
                                      return;
                                    }
                                    const resp = await axios.delete(
                                      `${API}/appointments/${editingAppointment.id}`
                                    );
                                    addToUndoStack('delete', editingAppointment);
                                    toast.success(resp.data?.message || 'Appointment deleted');
                                    setShowAppointmentForm(false);
                                    setEditingAppointment(null);
                                    refreshAppointments(true); refreshStats(); refreshNotes();
                                  } catch (error) {
                                    console.error('Error deleting appointment:', error);
                                    const detail = error.response?.data?.detail;
                                    // Billed/paid note block, claim-linked block, permission, etc.
                                    toast.error(
                                      typeof detail === 'string' ? detail :
                                      (detail?.message || 'Failed to delete appointment'),
                                      { duration: 7000 }
                                    );
                                    refreshAppointments(true);
                                  }
                                }}
                              >
                                Delete Appointment
                                    </Button>
                                  </>
                                );
                              })()}
                              {editingAppointment && editingAppointment.appointment_type === 'group' && editingAppointment.selected_group_id && (
                                <>
                                  <Button 
                                    type="button" 
                                    variant="outline"
                                    size="sm"
                                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (typeof handleOpenAttendanceDialog === 'function') {
                                        handleOpenAttendanceDialog(editingAppointment);
                                      } else {
                                        openLocalAttendance(editingAppointment);
                                      }
                                    }}
                                  >
                                    📋 Attendance
                                  </Button>
                                  <Button 
                                    type="button" 
                                    variant="outline"
                                    size="sm"
                                    className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setAddAttendeeApptLocal(editingAppointment);
                                      setAddAttendeeSearchLocal('');
                                      setShowAddAttendeeLocal(true);
                                    }}
                                    data-testid="appt-add-attendee-btn"
                                  >
                                    <UserPlus className="h-4 w-4 mr-1" />
                                    Attendee
                                  </Button>
                                </>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2 ml-auto">
                              <Button type="button" variant="outline" size="sm" onClick={() => {
                                setShowAppointmentForm(false);
                                setEditingAppointment(null);
                              }}>
                                Cancel
                              </Button>
                              
                              {/* Create Note Now / View Note button - only for existing appointments that require documentation (client_session or group) */}
                              {editingAppointment && (appointmentFormData.appointment_type === 'client_session' || appointmentFormData.appointment_type === 'group') && (() => {
                                // Merge local notes with on-open hydration so this works even
                                // when opening an appointment from the Schedule tab with no
                                // client selected (notes state wouldn't contain this appt's
                                // notes otherwise).
                                const _hydratedNotes = appointmentNotesCache.appointmentId === editingAppointment.id
                                  ? appointmentNotesCache.notes
                                  : [];
                                const linkedNote =
                                  notes.find(n => n.appointment_id === editingAppointment.id) ||
                                  _hydratedNotes.find(n => n.appointment_id === editingAppointment.id);
                                // `created_from_note` is the appointment's provenance —
                                // the ID of the note it was auto-created from. We use it
                                // as a fallback when the note isn't in local `notes` state
                                // yet (so we don't hide a valid View Note button just
                                // because the list hasn't loaded). BUT if we do have the
                                // referenced note locally, trust its current
                                // `appointment_id` — that way an unlinked note correctly
                                // hides the button.
                                const createdFromNoteId = editingAppointment.created_from_note;
                                let hasCreatedFromNote = false;
                                if (createdFromNoteId) {
                                  const candidate = notes.find(n => n.id === createdFromNoteId);
                                  if (candidate) {
                                    hasCreatedFromNote = candidate.appointment_id === editingAppointment.id;
                                  } else {
                                    // Note not in local state — trust the provenance field.
                                    hasCreatedFromNote = true;
                                  }
                                }
                                const isGroupAppointment = appointmentFormData.appointment_type === 'group';
                                
                                // For group appointments, check for batch notes (individual notes per member).
                                // Merge the local `notes` state (current client's notes) with the
                                // per-appointment fetch result so the gate works even when opening
                                // a group appointment from the Schedule tab.
                                const hydratedNotes = appointmentNotesCache.appointmentId === editingAppointment.id
                                  ? appointmentNotesCache.notes
                                  : [];
                                const combinedApptNotes = [
                                  ...notes.filter(n => n.appointment_id === editingAppointment.id),
                                  ...hydratedNotes.filter(h => !notes.some(n => n.id === h.id)),
                                ];
                                const groupBatchNotes = isGroupAppointment ? combinedApptNotes : [];
                                
                                if (linkedNote || groupBatchNotes.length > 0 || hasCreatedFromNote) {
                                  // Note(s) exist - show View Note button with count for groups
                                  const noteCount = groupBatchNotes.length || 1;
                                  const isGroup = isGroupAppointment && groupBatchNotes.length > 0;
                                  // Check if all group notes are signed
                                  const allNotesSigned = isGroup && groupBatchNotes.every(n => 
                                    n.status === 'signed' || n.provider_signed_at
                                  );
                                  
                                  return (
                                    <div className="flex items-center gap-2">
                                      <Button 
                                        type="button" 
                                        variant="outline"
                                        className={isGroup ? "text-purple-600 border-purple-300 hover:bg-purple-50" : "text-blue-600 border-blue-300 hover:bg-blue-50"}
                                        onClick={async () => {
                                          if (isGroup) {
                                            // Open GroupNoteCreationDialog in edit mode for group notes
                                            const groupId = editingAppointment.selected_group_id || editingAppointment.group_id;
                                            let groupData = clientGroups.find(g => g.id === groupId);
                                            
                                            // If group not found locally, try to fetch it from the API
                                            if (!groupData && groupId) {
                                              try {
                                                void 0 && console.log('[GroupNotes] Fetching group from API for edit mode:', groupId);
                                                const groupResponse = await axios.get(`${API}/client-groups/${groupId}`);
                                                groupData = groupResponse.data;
                                              } catch (error) {
                                                console.error('[GroupNotes] Failed to fetch group:', error);
                                                toast.error('Could not find group information. Please refresh and try again.');
                                                return;
                                              }
                                            }
                                            
                                            // Fetch curriculum session data if group has curriculum
                                            let curriculumData = null;
                                            if (groupData?.curriculum_id) {
                                              try {
                                                const response = await axios.get(`${API}/client-groups/${groupId}/current-session`);
                                                curriculumData = response.data;
                                              } catch (error) {
                                                void 0 && console.log('No curriculum session data available');
                                              }
                                            }
                                            
                                            // P1 BUG FIX: Fetch FULL notes with content instead of using summary notes
                                            // The groupBatchNotes comes from the main notes list which uses summary=true
                                            // and doesn't include content. We need to fetch full notes for edit mode.
                                            let fullGroupNotes = [];
                                            try {
                                              void 0 && console.log('[GroupNotes] Fetching full notes with content for edit mode');
                                              const notesResponse = await axios.get(`${API}/notes?appointment_id=${editingAppointment.id}&limit=100&include_content=true`);
                                              fullGroupNotes = notesResponse.data || [];
                                              void 0 && console.log(`[GroupNotes] Fetched ${fullGroupNotes.length} notes with content`);
                                            } catch (error) {
                                              console.error('[GroupNotes] Failed to fetch full notes:', error);
                                              // Fall back to summary notes if fetch fails
                                              fullGroupNotes = groupBatchNotes;
                                            }
                                            
                                            setGroupNoteAppointment(editingAppointment);
                                            setGroupNoteGroup(groupData);
                                            setGroupNoteCurriculumSession(curriculumData);
                                            setGroupNoteExistingNotes(fullGroupNotes);
                                            setGroupNoteMode('edit');
                                            setShowGroupNoteDialog(true);
                                            setShowAppointmentForm(false);
                                          } else {
                                            // Individual note - open with proper initialization
                                            // Note opens as modal - stay on current tab
                                            setShowAppointmentForm(false);
                                            const noteToOpen = linkedNote || (createdFromNoteId ? {id: createdFromNoteId} : null);
                                            if (noteToOpen) {
                                              try {
                                                await openNote(noteToOpen);
                                              } catch (err) {
                                                // Auto-heal stale provenance: if the note
                                                // the appointment points to no longer exists,
                                                // clear `created_from_note` so the button
                                                // disappears next time.
                                                if (err?.response?.status === 404 && createdFromNoteId) {
                                                  try {
                                                    await axios.put(`${API}/appointments/${editingAppointment.id}`, {
                                                      created_from_note: null
                                                    });
                                                    setEditingAppointment(prev => prev ? {...prev, created_from_note: null} : prev);
                                                    toast.error('This note no longer exists. The stale link has been removed.');
                                                  } catch (e2) {
                                                    console.warn('[ViewNote] Failed to clear stale created_from_note:', e2);
                                                    toast.error('Failed to load note details');
                                                  }
                                                } else {
                                                  toast.error('Failed to load note details');
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      >
                                        {isGroup ? <Users className="h-4 w-4 mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                                        {isGroup 
                                          ? ((allNotesSigned || (!user?.is_provider && !user?.is_supervisor)) ? `View ${noteCount} Notes` : `View & Sign ${noteCount} Notes`)
                                          : 'View Note'} →
                                      </Button>
                                      {isGroup && (
                                        <span className="text-xs text-gray-500">
                                          (1 per member)
                                        </span>
                                      )}
                                    </div>
                                  );
                                } else if (isGroupAppointment) {
                                  // Group appointment - show Create Group Notes button
                                  const groupId = editingAppointment.selected_group_id || editingAppointment.group_id;
                                  const group = clientGroups.find(g => g.id === groupId);
                                  
                                  // Debug logging
                                  void 0 && console.log('[GroupNotes] Group lookup:', { groupId, foundGroup: !!group, clientGroupsCount: clientGroups?.length });
                                  
                                  if (!group) {
                                    console.warn('[GroupNotes] Group not found in clientGroups:', { 
                                      groupId, 
                                      availableGroupIds: clientGroups?.map(g => g.id).slice(0, 5) 
                                    });
                                  }
                                  
                                  return (
                                    <Button 
                                      type="button" 
                                      variant="outline"
                                      className="text-purple-600 border-purple-300 hover:bg-purple-50"
                                      onClick={async () => {
                                        // FIRST: Check backend for any existing notes for this appointment
                                        // This prevents duplicates when another user already started notes
                                        let existingGroupNotes = [];
                                        try {
                                          const existingNotesResponse = await axios.get(`${API}/notes?appointment_id=${editingAppointment.id}&limit=100&summary=true`);
                                          existingGroupNotes = existingNotesResponse.data || [];
                                        } catch (error) {
                                          void 0 && console.log('Could not check for existing notes:', error);
                                        }
                                        
                                        // If group not found locally, try to fetch it from the API
                                        let groupData = group;
                                        if (!groupData && groupId) {
                                          try {
                                            void 0 && console.log('[GroupNotes] Fetching group from API:', groupId);
                                            const groupResponse = await axios.get(`${API}/client-groups/${groupId}`);
                                            groupData = groupResponse.data;
                                            void 0 && console.log('[GroupNotes] Fetched group:', groupData?.name);
                                          } catch (error) {
                                            console.error('[GroupNotes] Failed to fetch group:', error);
                                            toast.error('Could not find group information. Please refresh and try again.');
                                            return;
                                          }
                                        }
                                        
                                        if (!groupData) {
                                          toast.error('Group not found. Please refresh and try again.');
                                          return;
                                        }
                                        
                                        // Fetch curriculum session data if group has curriculum
                                        let curriculumData = null;
                                        if (groupData?.curriculum_id) {
                                          try {
                                            const response = await axios.get(`${API}/client-groups/${groupId}/current-session`);
                                            curriculumData = response.data;
                                          } catch (error) {
                                            void 0 && console.log('No curriculum session data available');
                                          }
                                        }
                                        
                                        setGroupNoteAppointment(editingAppointment);
                                        setGroupNoteGroup(groupData);
                                        setGroupNoteCurriculumSession(curriculumData);
                                        
                                        // If notes already exist, open in edit mode instead of create
                                        if (existingGroupNotes.length > 0) {
                                          setGroupNoteExistingNotes(existingGroupNotes);
                                          setGroupNoteMode('edit');
                                          toast.info(`Found ${existingGroupNotes.length} existing note(s) - opening in edit mode`);
                                        } else {
                                          setGroupNoteExistingNotes([]);
                                          setGroupNoteMode('create');
                                        }
                                        setShowGroupNoteDialog(true);
                                        setShowAppointmentForm(false);
                                      }}
                                    >
                                      <Users className="h-4 w-4 mr-2" />
                                      Create Group Notes
                                    </Button>
                                  );
                                } else {
                                  // Individual appointment - show Create Note Now button
                                  return (
                                    <Button 
                                      type="button" 
                                      variant="outline"
                                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                      onClick={async () => {
                                        try {
                                          // Try to create note
                                          const response = await axios.post(`${API}/appointments/${editingAppointment.id}/auto-create-note`);
                                          if (response.data.note_id) {
                                            const templateName = response.data.template_name || 'selected template';
                                            toast.success(`Draft note created using ${templateName}`);
                                            
                                            // Close the appointment form first
                                            setShowAppointmentForm(false);
                                            
                                            // Fetch the newly created note directly and open it with proper initialization
                                            try {
                                              const noteResponse = await axios.get(`${API}/notes/${response.data.note_id}`);
                                              const newNote = noteResponse.data;
                                              
                                              // Add the new note to the notes list so UI updates immediately
                                              // This ensures "Create Note Now" changes to "View Note" without needing a tab switch
                                              setNotes(prev => [newNote, ...prev]);
                                              setClientNotes(prev => [newNote, ...prev]);
                                              
                                              // Use openNote to properly initialize content from template
                                              // Note opens as a modal dialog - no need to switch tabs
                                              // User stays on current tab (schedule) so closing the note returns them there
                                              await openNote(newNote);
                                            } catch (fetchError) {
                                              console.error('Failed to fetch new note:', fetchError);
                                              // Fall back to just refreshing data
                                              refreshNotes(); refreshTodos();
                                            }
                                          }
                                        } catch (error) {
                                          if (error.response?.status === 400) {
                                            toast.error(error.response.data.detail || 'Cannot create note');
                                          } else {
                                            toast.error('Failed to create note');
                                          }
                                        }
                                      }}
                                    >
                                      <FileText className="h-4 w-4 mr-2" />
                                      Create Note Now
                                    </Button>
                                  );
                                }
                              })()}
                              
                              <Button type="submit" data-testid="create-appointment-submit" className="bg-teal-600 hover:bg-teal-700">
                                {editingAppointment ? 'Update Appointment' : 'Schedule Appointment'}
                              </Button>
                            </div>
                          </div>

                          {/* Suppress reminders toggle (Feb 2026 user
                              request).  Bulk-imported appointments
                              default suppress=true so the next
                              scheduler tick doesn't fire dozens of
                              reminders at once.  Surface the toggle
                              here so providers can flip it back on
                              when they decide they actually want
                              reminders to go out.  Editing-only —
                              new appointments default to reminders
                              ON. */}
                          {editingAppointment && (
                            <div className="flex items-center gap-2 pt-3 border-t mt-3 text-sm">
                              <input
                                id="appt-suppress-reminders"
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300"
                                checked={!!appointmentFormData.suppress_reminders}
                                onChange={(e) => setAppointmentFormData({
                                  ...appointmentFormData,
                                  suppress_reminders: e.target.checked,
                                })}
                                data-testid="appt-suppress-reminders"
                              />
                              <Label htmlFor="appt-suppress-reminders" className="cursor-pointer">
                                Suppress reminders for this appointment
                                <span className="text-xs text-gray-500 ml-2">
                                  (no 48h / 2h / 5min notifications will be sent)
                                </span>
                              </Label>
                            </div>
                          )}
                          
                          {/* Created/Modified info - only show when editing */}
                          {editingAppointment && (
                            <AppointmentLinkedItemsPanel
                              appointmentId={editingAppointment.id}
                              onOpenNote={(noteId) => {
                                if (noteId) {
                                  // Close this dialog and route to the
                                  // note via the existing handler.
                                  closeAppointment();
                                  // Jun 2026: on V2 pages open the native
                                  // V2 editor instead of the legacy mount.
                                  const onV2 = window.location.pathname.startsWith('/v2');
                                  const ret = encodeURIComponent(window.location.pathname + window.location.search);
                                  setTimeout(() => {
                                    if (onV2) { navigate(`/v2/note/${noteId}?return=${ret}`); return; }
                                    if (!(openNoteById && openNoteById(noteId))) navigate(`/note/${noteId}`);
                                  }, 50);
                                }
                              }}
                            />
                          )}
                          {editingAppointment && (
                            <div className="text-xs text-gray-400 pt-2 border-t mt-4 flex items-center justify-between">
                              <button
                                type="button"
                                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 underline underline-offset-2"
                                onClick={() => {
                                  const entity = {
                                    type: 'appointment',
                                    id: editingAppointment.id,
                                    name: `Appointment — ${new Date(editingAppointment.start_time).toLocaleDateString()}`
                                  };
                                  if (typeof setChangeHistoryEntity === 'function' && typeof setShowChangeHistory === 'function') {
                                    setChangeHistoryEntity(entity);
                                    setShowChangeHistory(true);
                                  } else {
                                    setLocalHistoryEntity(entity);
                                  }
                                }}
                              >
                                <History className="h-3 w-3" />
                                Change History
                              </button>
                              <div className="text-right">
                              {editingAppointment.created_at && (
                                <span>
                                  Created {new Date(editingAppointment.created_at).toLocaleDateString('en-US', { 
                                    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
                                  })}
                                  {editingAppointment.created_by && users?.find(u => u.id === editingAppointment.created_by) && (
                                    <> by {users.find(u => u.id === editingAppointment.created_by)?.full_name || 
                                           `${users.find(u => u.id === editingAppointment.created_by)?.first_name} ${users.find(u => u.id === editingAppointment.created_by)?.last_name}`}</>
                                  )}
                                </span>
                              )}
                              {editingAppointment.updated_at && editingAppointment.updated_at !== editingAppointment.created_at && (
                                <span className="ml-3">
                                  • Modified {new Date(editingAppointment.updated_at).toLocaleDateString('en-US', { 
                                    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
                                  })}
                                </span>
                              )}
                              </div>
                            </div>
                          )}
                        </form>
                        )}
                      </DialogContent>
                    </Dialog>
    {/* Mar 2026 — Prefill-Before-Send workflow.  Opens after the
        provider clicks "Prepare & Send Intake Bundle"; lets them fill
        in any provider-side fields (e.g. "consent is for {name}")
        BEFORE the client is notified. */}
    <IntakeBundlePrefillDialog
      open={!!appointmentFormData._prefillOpen}
      onClose={() => setAppointmentFormData(prev => ({ ...prev, _prefillOpen: false }))}
      appointmentId={editingAppointment?.id}
      clientId={appointmentFormData._prefillClientId}
      bundleId={appointmentFormData._prefillBundleId}
      bundleName={appointmentFormData._prefillBundleName}
      onSent={(result) => {
        if (result?.sent) {
          const sentAt = result.intake_bundle_sent_at || new Date().toISOString();
          const bundleId = appointmentFormData._prefillBundleId || appointmentFormData.intake_bundle_id;
          setAppointmentFormData(prev => ({
            ...prev,
            intake_bundle_sent_at: sentAt,
            intake_bundle_sent_by: user?.id || prev.intake_bundle_sent_by,
            _prefillOpen: false,
          }));
          // Keep the appointments list + editingAppointment in sync so the
          // sent badge survives closing/reopening the detail dialog.
          const apptId = editingAppointment?.id;
          if (apptId) {
            const patch = {
              intake_bundle_id: bundleId,
              intake_bundle_sent_at: sentAt,
              intake_bundle_sent_by: user?.id,
            };
            setAppointments(prev => prev.map(a => (a.id === apptId ? { ...a, ...patch } : a)));
            setEditingAppointment(prev => (prev && prev.id === apptId ? { ...prev, ...patch } : prev));
          }
        }
      }}
    />
    {/* After-hours scheduling confirmation (Jun 2026) */}
    <AlertDialog open={showAfterHoursConfirm} onOpenChange={setShowAfterHoursConfirm}>
      <AlertDialogContent data-testid="after-hours-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Outside Business Hours</AlertDialogTitle>
          <AlertDialogDescription>
            This appointment falls outside your practice's normal business hours
            ({formatHHMM12(businessHours.start)} – {formatHHMM12(businessHours.end)}).
            Do you want to schedule it anyway?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="after-hours-cancel-btn">Go Back</AlertDialogCancel>
          <AlertDialogAction
            className="bg-amber-600 hover:bg-amber-700"
            onClick={confirmAfterHoursSave}
            data-testid="after-hours-confirm-btn"
          >
            Schedule Anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    {/* Linked-note delete confirmation (Jun 2026) — in-app replacement for
        the old "Cancel to unlink" browser alert. Three clear actions. */}
    <AlertDialog
      open={!!linkedNoteDeleteInfo}
      onOpenChange={(o) => { if (!o) setLinkedNoteDeleteInfo(null); }}
    >
      <AlertDialogContent data-testid="delete-linked-note-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>This appointment has a linked note</AlertDialogTitle>
          <AlertDialogDescription>
            A clinical note (status: {linkedNoteDeleteInfo?.noteStatus || 'draft'}) is linked
            to this appointment. You can delete both, or delete only the appointment —
            the note stays in the chart with its Date of Service preserved.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-linked-cancel-btn">Cancel</AlertDialogCancel>
          <Button
            type="button"
            variant="outline"
            onClick={() => performLinkedNoteDelete(false)}
            data-testid="delete-linked-unlink-btn"
          >
            Unlink Note
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => performLinkedNoteDelete(true)}
            data-testid="delete-linked-both-btn"
          >
            Delete Both
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <ChangeHistoryModal
      open={!!localHistoryEntity}
      onOpenChange={(o) => { if (!o) setLocalHistoryEntity(null); }}
      entityType="appointment"
      entityId={localHistoryEntity?.id}
      entityName={localHistoryEntity?.name}
    />
    <AttendanceTrackingDialog
      open={localAttendanceOpen}
      onOpenChange={setLocalAttendanceOpen}
      attendanceAppointment={localAttendanceApt}
      attendanceData={localAttendanceData}
      setAttendanceData={setLocalAttendanceData}
      clientGroups={[...(clientGroups || []), ...(localAttendanceGroup ? [localAttendanceGroup] : [])]}
      clients={allClientsForLookup || clients || []}
      onSaveAttendance={saveLocalAttendance}
    />
    <Dialog open={showAddAttendeeLocal} onOpenChange={(open) => {
      setShowAddAttendeeLocal(open);
      if (!open) {
        setAddAttendeeSearchLocal('');
        setAddAttendeeApptLocal(null);
      }
    }}>
      <DialogContent className="max-w-md" data-testid="add-attendee-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-green-600" />
            Add Attendee to Session
          </DialogTitle>
          <DialogDescription>
            Add a drop-in or guest to this specific session only. They won't be added to the group roster.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {addAttendeeApptLocal && (
            <div>
              <Label className="text-sm font-medium">Current Attendees</Label>
              <div className="mt-1 flex flex-wrap gap-1">
                {(addAttendeeApptLocal.client_ids || []).map(clientId => {
                  const client = (allClientsForLookup || clients || []).find(c => c.id === clientId);
                  return client ? (
                    <div key={clientId} className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                      <span>{client.full_name}</span>
                      <button
                        className="ml-1 hover:text-red-600"
                        data-testid={`remove-attendee-${clientId}`}
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/appointments/${addAttendeeApptLocal.id}/remove-attendee`, null, {
                              params: { client_id: clientId }
                            });
                            toast.success(`Removed ${client.full_name} from session`);
                            setAddAttendeeApptLocal(prev => ({
                              ...prev,
                              client_ids: prev.client_ids.filter(id => id !== clientId)
                            }));
                            refreshAppointments(true); refreshStats();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to remove attendee');
                          }
                        }}
                        title="Remove from this session"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : null;
                })}
                {(!addAttendeeApptLocal.client_ids || addAttendeeApptLocal.client_ids.length === 0) && (
                  <span className="text-sm text-gray-500">No attendees yet</span>
                )}
              </div>
            </div>
          )}

          <div>
            <Label className="text-sm font-medium">Add Client</Label>
            <DebouncedInput
              placeholder="Search clients by name..."
              value={addAttendeeSearchLocal}
              onChange={(e) => setAddAttendeeSearchLocal(e.target.value)}
              className="mt-1"
              data-testid="add-attendee-search-input"
            />

            {addAttendeeSearchLocal && (
              <div className="mt-2 max-h-48 overflow-y-auto border rounded">
                {(allClientsForLookup || clients || [])
                  .filter(c =>
                    c.full_name?.toLowerCase().includes(addAttendeeSearchLocal.toLowerCase()) &&
                    !addAttendeeApptLocal?.client_ids?.includes(c.id)
                  )
                  .slice(0, 10)
                  .map(client => (
                    <div
                      key={client.id}
                      className="px-3 py-2 hover:bg-green-50 cursor-pointer flex items-center justify-between"
                      data-testid={`add-attendee-option-${client.id}`}
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/appointments/${addAttendeeApptLocal.id}/add-attendee`, null, {
                            params: { client_id: client.id }
                          });
                          toast.success(`Added ${client.full_name} to session`);
                          setAddAttendeeApptLocal(prev => ({
                            ...prev,
                            client_ids: [...(prev.client_ids || []), client.id]
                          }));
                          setAddAttendeeSearchLocal('');
                          refreshAppointments(true); refreshStats();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to add attendee');
                        }
                      }}
                    >
                      <span>{client.full_name}</span>
                      <UserPlus className="h-4 w-4 text-green-600" />
                    </div>
                  ))}
                {(allClientsForLookup || clients || []).filter(c =>
                  c.full_name?.toLowerCase().includes(addAttendeeSearchLocal.toLowerCase()) &&
                  !addAttendeeApptLocal?.client_ids?.includes(c.id)
                ).length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">No matching clients found</div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShowAddAttendeeLocal(false)} data-testid="add-attendee-done-btn">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default AppointmentScheduleDialog;
